import { warn } from "@shared/lib/journal";
import { useEffect, useMemo, type ReactNode } from "react";
import { create } from "zustand";
import { ApiError, api } from "@shared/api";
import { keys, queryClient } from "@shared/api";
import { forgetQuick } from "@shared/lib/emoji";
import { assertCanStartRestartSensitiveOperation, useRestartBlocker } from "@shared/lib/desktop";
import { plainText, silentPrefix } from "@shared/lib/markup";
import { notify } from "@shared/lib/notify";
import { messageLink } from "@shared/lib/jump";
import { cue, heldRoom, playSound, type VoiceTake } from "@shared/media";
import { calls as shouldCall } from "@entities/channel";
import { realtime, type ConnectionState } from "@shared/realtime";
import type {
  SoundPlayed,
  CallRing,
  DirectChannel,
  Message,
  MessageAuthor,
  NotifyLevel,
  PollDraft,
  PublicUser,
  RealtimeEvent,
  ServerState,
  ServerSummary,
  Thread,
  ThreadListItem,
  Uuid,
} from "@shared/api";
import { useSession } from "@entities/session";
import { useCatalogue, type CallMember } from "@entities/workspace";

/** Сообщение, которого сервер ещё не подтвердил. */
export interface Pending {
  key: string;
  channelId: Uuid;
  content: string;
  failed: boolean;
  /** Почему не ушло, если сервер сказал: АвтоМод, медленный режим. */
  reason?: string;
  /**
   * Что приложено. Хранится здесь по той же причине, что и цитата: повтор
   * обязан отправить то же самое.
   *
   * До этого повтор слал пустой список, и вложения терялись молча. Пока текст
   * был обязателен, это выглядело как «картинка отвалилась»; с сообщением из
   * одного файла повтор стал отправлять пустоту, которую сервер отвергает
   * навсегда.
   */
  attachmentIds: Uuid[];
  /** На что отвечает. Хранится здесь, чтобы повтор не потерял цитату. */
  replyToId: Uuid | null;
  /** Звать ли автора оригинала — и повтор зовёт так же. */
  mentionAuthor: boolean;
  /** Какие вложения — спойлеры; повтор помечает те же. */
  spoilerIds?: Uuid[];
  /** Описания вложений; повтор шлёт те же. */
  descriptions?: { id: Uuid; text: string }[];
}

/** Отказ сервера словами — для строки «не ушло». Сбой сети причины не знает. */
export function refusal(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  if (error.code === "automod") return "АвтоМод сервера не пропустил слово";
  if (error.code === "automod_mentions") return "АвтоМод: слишком много упоминаний в одном сообщении";
  if (error.code === "slowmode") return "медленный режим";
  return undefined;
}

interface StoreValue {
  servers: ServerSummary[];
  states: Record<Uuid, ServerState | undefined>;
  directs: DirectChannel[];
  relationships: PublicUser[];
  /** Кто сейчас на связи. Приходит событиями, поэтому держится отдельно. */
  online: Set<Uuid>;
  /** Состав звонков по каналам. */
  calls: Record<Uuid, CallMember[]>;
  /** Кого вызывают в звонок переписки (`useCatalogue`). */
  rings: Record<Uuid, CallRing[]>;
  /** Попросили войти в звонок — из строки «присоединиться» в ленте. */
  callRequest: Uuid | null;
  requestCall: (channelId: Uuid | null) => void;
  /** С какого сервера нас убрали и был ли это бан. `null` — сообщение закрыто. */
  removedFrom: { serverId: Uuid; banned: boolean } | null;
  setRemovedFrom: (removed: { serverId: Uuid; banned: boolean } | null) => void;
  /** Модератор велел перейти в другой голосовой канал. */
  movedTo: { from: Uuid; to: Uuid } | null;
  setMovedTo: (move: { from: Uuid; to: Uuid } | null) => void;
  /**
   * Счётчик изменений состава сервера.
   *
   * Сам состав держит экран: он нужен только там, где сервер открыт. Сюда
   * приходит лишь весть о том, что список устарел, — экран смотрит на число
   * своего сервера и перезапрашивает, когда оно сдвинулось.
   */
  /** Сколько в канале непрочитанного. Канала нет в записи — значит ноль. */
  unread: Record<Uuid, number>;
  /**
   * Сколько из непрочитанного адресовано лично — с упоминанием хэндла.
   *
   * Отдельно от общего счётчика: «двадцать сообщений» и «одно, и оно вам» —
   * разные новости, и мерить их одним числом значит потерять вторую.
   */
  mentions: Record<Uuid, number>;
  /**
   * Сколько непрочитанного на сервере — сумма по его каналам.
   *
   * Считается здесь, а не в ряду серверов: тому иначе пришлось бы знать, какие
   * каналы чьи, то есть загрузить состояние всех серверов сразу.
   */
  serverUnread: Record<Uuid, number>;
  /** Сколько на сервере адресовано лично. */
  serverMentions: Record<Uuid, number>;
  connection: ConnectionState;
  attempt: number;

  loadServer: (id: Uuid) => Promise<ServerState | undefined>;
  reloadServers: () => Promise<void>;
  reloadDirects: () => Promise<void>;
  reloadRelationships: () => Promise<void>;
  loadCall: (channelId: Uuid) => Promise<void>;
  /**
   * Отметить канал прочитанным целиком, не читая.
   *
   * Это осознанное действие человека — пункт меню, — а не побочный итог
   * открытия канала: сам по себе он теперь ничего не прочитывает.
   */
  markRead: (channelId: Uuid) => void;
  /**
   * «Отметить непрочитанным» с этого сообщения — как в Discord. Пока человек
   * остаётся в канале, отметка не двигается от одного взгляда на ленту: он
   * её поставил нарочно.
   */
  markUnread: (channelId: Uuid, messageId: Uuid) => void;
  /** Ушли из канала по-настоящему (не фокус окна): ручная отметка больше не держится. */
  releaseKept: (channelId: Uuid) => void;
  /** Отметить прочитанным всё непрочитанное сервера. */
  markServerRead: (serverId: Uuid) => void;
  /**
   * Сообщение побывало на глазах: попало в видимую часть ленты или под
   * курсор.
   *
   * Отметка едет до него и не дальше. «Открыл канал» само по себе ничего не
   * прочитывает: пролистнуть двести сообщений вниз, не увидев их, — не то же
   * самое, что прочесть.
   */
  saw: (channelId: Uuid, messageId: Uuid) => void;
  /**
   * С какого сообщения начинается непрочитанное: перед ним и идёт черта.
   *
   * `undefined` — черты нет. Строка — черта стоит перед этим сообщением; если
   * его в окне не оказалось, граница лежит выше загруженного.
   *
   * Замеряется один раз, когда канал открывают, и дальше не двигается: если
   * гнать её за каждым новым сообщением, она уезжает из-под глаз ровно тогда,
   * когда человек читает.
   */
  newSince: (channelId: Uuid) => Uuid | undefined;
  /** Чей канал — по сводке непрочитанного: сервер, `null` — переписка, `undefined` — неизвестно. */
  serverOf: (channelId: Uuid) => Uuid | null | undefined;
  /** Уровень оклика, что действует в канале: свой, иначе сервера, иначе «Все сообщения». */
  levelIn: (channelId: Uuid) => NotifyLevel;
  /**
   * Какой канал человек сейчас читает. `null` — никакой.
   *
   * Нужно, чтобы приходящее в открытый канал не мигало счётчиком: без этого
   * засечка загоралась бы на каждое сообщение и тут же гасла.
   */
  setReading: (channelId: Uuid | null) => void;
  /**
   * Видно ли конец ленты.
   *
   * Открытый канал ещё не значит «прочитан»: можно уйти вверх по истории, и
   * приходящее внизу мимо глаз читать за человека нельзя.
   */
  setAtEnd: (atEnd: boolean) => void;

  messages: (channelId: Uuid) => Message[] | undefined;
  pending: (channelId: Uuid) => Pending[];
  /** Кто печатает в канале прямо сейчас. Без себя: своё событие не приходит. */
  typingIn: (channelId: Uuid) => Uuid[];
  loadMessages: (channelId: Uuid) => Promise<void>;
  loadOlder: (channelId: Uuid) => Promise<boolean>;
  /** Догрузить страницу вниз. `false` — дальше нечего. */
  loadNewer: (channelId: Uuid) => Promise<boolean>;
  /**
   * Доходит ли окно до самого свежего сообщения.
   *
   * Пока не доходит, лента — середина разговора: внизу у неё не конец, а край
   * загруженного, и об этом надо сказать вслух.
   */
  atLatest: (channelId: Uuid) => boolean;
  /** Доходит ли окно до самого первого сообщения канала: старше ничего нет. */
  atStart: (channelId: Uuid) => boolean;
  /**
   * Что выбрано про оклик — по каналам и по серверам.
   *
   * Отсутствие записи значит «как выше», а не «молчать», поэтому и хранится
   * как отсутствие: заполни умолчаниями — потеряешь разницу между «оставил как
   * есть» и «выбрал ровно то же самое», и смена умолчания сервера перестанет
   * доходить до каналов.
   */
  notifyChannel: Record<Uuid, NotifyLevel>;
  notifyServer: Record<Uuid, NotifyLevel>;
  /** Заглушённые на время — до какого; срок вышел — выбор снимается сам. */
  notifyUntil: Record<Uuid, string>;
  /** `null` — вернуть к умолчанию; `minutes` — «заглушить на время» (только с `none`). */
  setChannelNotify: (channelId: Uuid, level: NotifyLevel | null, minutes?: number | null) => Promise<void>;
  /** «Включить уведомления»: заглушённый на время возвращается к прежнему своему уровню. */
  unmuteChannel: (channelId: Uuid) => Promise<void>;
  setServerNotify: (serverId: Uuid, level: NotifyLevel | null) => Promise<void>;
  /**
   * «Закрепить канал вверху» — как в Discord: свои закреплённые каналы стоят
   * группой над остальными. Личное, хранится на сервере рядом с выбором оклика.
   */
  pinnedChannels: Record<Uuid, true>;
  setChannelPinned: (channelId: Uuid, on: boolean) => Promise<void>;

  /** Бросить окно в конец ленты. */
  jumpToLatest: (channelId: Uuid) => Promise<void>;
  /**
   * Просили ли конец ленты — и снимает вопрос, отвечая один раз.
   *
   * Отдельным признаком, а не выводом из «окно теперь доходит до конца»:
   * обычное открытие канала выглядит точно так же, и лента переставала бы
   * вставать на черту.
   */
  takeJump: (channelId: Uuid) => boolean;
  /**
   * Ставит окно вокруг названного сообщения.
   *
   * Нужно цитате: процитированное бывает старше окна, а окно скользит — у
   * того, кто отмотал далеко, оно вылетает быстро. Сама лента такое сообщение
   * найти не может, потому что его нет в разметке.
   */
  openAt: (channelId: Uuid, messageId: Uuid) => Promise<void>;
  /**
   * К какому сообщению лента должна подкрутиться после перестройки окна.
   *
   * Спрашивается один раз: подкрутиться надо на том проходе, где окно
   * приехало, а дальше человек листает сам.
   */
  takeJumpTo: (channelId: Uuid) => Uuid | null;
  send: (
    channelId: Uuid,
    content: string,
    attachmentIds: Uuid[],
    replyToId?: Uuid | null,
    /** Звать ли автора оригинала — «@ВКЛ» в Discord. По умолчанию зовём. */
    mentionAuthor?: boolean,
    /** Какие из вложений — спойлеры. */
    spoilerIds?: Uuid[],
    /** Описания вложений. */
    descriptions?: { id: Uuid; text: string }[],
  ) => Promise<void>;
  /** Стикер — отдельным сообщением, сразу, как в Discord. Отказ сервера — исключением. */
  sendSticker: (channelId: Uuid, stickerId: Uuid) => Promise<void>;
  /** Голосовое — отдельным сообщением: загрузить запись и отправить. Отказ — исключением. */
  sendVoice: (channelId: Uuid, take: VoiceTake, replyToId?: Uuid | null) => Promise<void>;
  /** Опрос — отдельным сообщением. Отказ сервера — исключением. */
  sendPoll: (channelId: Uuid, poll: PollDraft) => Promise<void>;
  /** Пост форума: заголовок и первое сообщение; обсуждение — в его ветке. */
  sendPost: (channelId: Uuid, title: string, content: string, tagIds?: Uuid[]) => Promise<void>;
  /** Свой голос в опросе целиком; пусто — снять. Отказ сервера — исключением. */
  vote: (channelId: Uuid, messageId: Uuid, answerIds: Uuid[]) => Promise<void>;
  /** «Завершить опрос» досрочно. Отказ сервера — исключением. */
  endPoll: (channelId: Uuid, messageId: Uuid) => Promise<void>;
  retry: (key: string) => Promise<void>;
  drop: (key: string) => void;
  edit: (channelId: Uuid, messageId: Uuid, content: string, removeAttachments?: Uuid[]) => Promise<void>;
  remove: (channelId: Uuid, messageId: Uuid) => Promise<void>;
  /** Переслать сообщение в канал или переписку. */
  forward: (targetChannelId: Uuid, messageId: Uuid, comment: string) => Promise<void>;
  /** Закрепить или открепить в канале. */
  setPinned: (channelId: Uuid, messageId: Uuid, pinned: boolean) => Promise<void>;
  /** Разослать объявление подписчикам канала. */
  publish: (channelId: Uuid, messageId: Uuid) => Promise<void>;
  /**
   * Ставит или снимает свой значок — по тому, стоит ли он сейчас.
   *
   * Лента меняется сразу, не дожидаясь ответа: нажатие на реакцию должно
   * ощущаться нажатием, а не запросом. Сорвалось — возвращаем как было.
   */
  toggleReaction: (channelId: Uuid, messageId: Uuid, emoji: string, mine: boolean) => Promise<void>;
  /**
   * На что отвечает набираемое сообщение. Держится по каналам: уйти в
   * соседний и вернуться, потеряв цитату, было бы неожиданно.
   */
  replyTo: (channelId: Uuid) => Message | undefined;
  setReplyTo: (channelId: Uuid, message: Message | null) => void;

  /**
   * Сколько веток канала ждут прочтения — и только тех, за которыми следим.
   *
   * Отдельно от `unread`: засечка и светлое имя значат «новое в самом
   * канале», у веток свой знак, и смешать их значило бы лишить смысла оба.
   */
  threadUnread: Record<Uuid, number>;

  /**
   * Что открыто в третьей колонке. Одна на всё окно: это колонка, а не окно.
   *
   * `kind: "thread"` — сама ветка, `kind: "list"` — список тредов канала.
   * У ветки может не быть `threadId`: пока в ней нет ни одного ответа, её нет
   * и на сервере — она живёт только здесь.
   */
  panel: ThreadPanelState | null;
  /** `full` — сразу на весь экран, как ветка из списка каналов в Discord. */
  openThread: (channelId: Uuid, rootId: Uuid, threadId: Uuid | null, full?: boolean) => void;
  /** «Открыть на весь экран» и обратно в колонку — у уже открытой ветки. */
  expandThread: (full: boolean) => void;
  /** Перечитать ветку, если она уже загружена: её поменяли меню или окно настроек. */
  reloadThread: (channelId: Uuid, threadId: Uuid) => void;
  openThreadList: (channelId: Uuid) => void;
  /** Загрузить список веток канала, не открывая колонку, — для окна веток в шапке. */
  loadThreads: (channelId: Uuid) => void;
  closePanel: () => void;

  /** Ветка в открытой панели. `undefined` — ещё не пришла. */
  thread: (threadId: Uuid) => Thread | undefined;
  threadMessages: (threadId: Uuid) => Message[] | undefined;
  threadList: (channelId: Uuid) => ThreadListItem[] | undefined;
  /** Фильтр списка: «слежу» или «все». */
  threadFilter: boolean;
  setThreadFilter: (followed: boolean) => void;

  /** Ответить в открытой ветке. Первый ответ её и заводит. */
  /**
   * `title` — имя с шага «Новая ветка»: введённое или подсказка текстом на
   * момент создания. Ставится, когда первый ответ ветку завёл. `extra` — то,
   * что ещё бывает у сообщения в канале: спойлеры, описания, голосовое, стикер.
   */
  sendInThread: (
    content: string,
    attachmentIds: Uuid[],
    title?: string | null,
    extra?: ThreadExtra,
  ) => Promise<void>;
  /*
   * Канал передаётся явно, а не выводится из открытой панели: меню ветки
   * открывается прямо из ленты, и панель при этом закрыта. Пока канал брался
   * оттуда, эти три действия молча не делали ничего.
   */
  followThread: (channelId: Uuid, threadId: Uuid, follow: boolean) => Promise<void>;
  setThreadNotify: (channelId: Uuid, threadId: Uuid, level: NotifyLevel | null) => Promise<void>;
  markThreadRead: (channelId: Uuid, threadId: Uuid, messageId: Uuid) => Promise<void>;
}

/** Что ещё несёт ответ в ветке — то же, что сообщение в канале, кроме опроса. */
export interface ThreadExtra {
  /** Какие из вложений — спойлеры. */
  spoilerIds?: Uuid[];
  /** Описания вложений. */
  descriptions?: { id: Uuid; text: string }[];
  /** Голосовое: запись загружается и уходит единственным вложением. */
  voice?: VoiceTake;
  stickerId?: Uuid;
}

/** Что показывает третья колонка. */
export interface ThreadPanelState {
  channelId: Uuid;
  kind: "thread" | "list";
  /** Корень ветки. У списка его нет. */
  rootId?: Uuid;
  /** `null` — ветки ещё нет: в ней не написали ни одного ответа. */
  threadId?: Uuid | null;
  /** Ветка на месте ленты канала, а не колонкой справа — «Открыть на весь экран». */
  full?: boolean;
}

/** Ключи общего значения, за которые отвечает каталог. */
type FromCatalogue =
  | "servers"
  | "states"
  | "directs"
  | "relationships"
  | "online"
  | "calls"
  | "rings"
  | "callRequest"
  | "requestCall"
  | "connection"
  | "attempt"
  | "loadServer"
  | "reloadServers"
  | "reloadDirects"
  | "reloadRelationships"
  | "loadCall"
  | "removedFrom"
  | "movedTo"
  | "setMovedTo"
  | "setRemovedFrom";

/**
 * Вторая половина общего значения: счётчики, окна ленты, ветки.
 *
 * Сверх того, что видят экраны, здесь лежит сырое состояние — то, из которого
 * они читают выборками (`messages`, `thread`, `pending`). В контексте оно
 * пряталось в замыкании; стор отдаёт состояние целиком, и спрятать его негде.
 */
interface Chat extends Omit<StoreValue, FromCatalogue> {
  since: Record<Uuid, Uuid | undefined>;
  feeds: Record<Uuid, Message[] | undefined>;
  queue: Pending[];
  /**
   * Кто печатает: канал → человек → до какого мига держать надпись.
   *
   * Сроком, а не признаком: «перестал печатать» никто не присылает.
   */
  typing: Record<Uuid, Record<Uuid, number>>;
  replies: Record<Uuid, Message | undefined>;
  threads: Record<Uuid, Thread>;
  threadFeeds: Record<Uuid, Message[]>;
  threadLists: Record<Uuid, ThreadListItem[]>;

  /** Подписаться на события. Отдаёт отписку — как и сам `realtime.on`. */
  listen: () => (() => void) | undefined;
  /** Запросить то, что нужно сразу после входа. */
  start: () => void;
  /**
   * Забыть всё.
   *
   * Прежде это выходило само: провайдер держал состояние и уходил вместе с
   * ним. Стор живёт в модуле и сам не уйдёт — без явной чистки следующий
   * вошедший на той же вкладке увидел бы чужую переписку.
   */
  forget: () => void;
}

const PAGE = 50;

/**
 * Сколько сообщений держим в окне.
 *
 * Окно росло без предела: подгрузка вверх подмешивала страницу и не
 * выбрасывала ничего. Сотня страниц — пять тысяч строк в разметке, и дело даже
 * не в их числе: `Feed` пересобирает их все на каждое событие в канале, а
 * событий там чужие сообщения, реакции и отметки прочтения.
 *
 * Триста — это шесть страниц: на любом экране это четыре-пять высот, то есть
 * пролистать окно насквозь быстрее, чем подгрузится следующее. Меньше брать
 * нельзя: подгрузка идёт за сто двадцать пикселей до края, и на коротком окне
 * человек упирался бы в неё, не успев прочитать.
 *
 * Отрезанное не теряется — оно лежит на сервере и приезжает обратно тем же
 * запросом, каким приехало впервые.
 */
const KEEP = 300;

/**
 * Сколько брать при открытии канала с непрочитанным.
 *
 * Больше страницы намеренно: окно встаёт вокруг черты, и половина уходит на
 * прочитанное над ней. Возьми ровно страницу — под чертой окажется два
 * десятка строк, которые на высоком экране кончаются, не начавшись.
 */
const OPEN = 100;

/**
 * Потолок счёта непрочитанного — тот же, что на сервере.
 *
 * Держится в двух местах намеренно: сервер решает, докуда считать, клиент —
 * когда переспросить. Разъедутся — клиент либо переспросит зря, либо не
 * переспросит вовсе; первое дёшево, второе заметно, поэтому число одно.
 */
const UNREAD_CAP = 1000;

/**
 * Нерисуемая бухгалтерия.
 *
 * Всё это прежде лежало в `useRef` — не из-за React, а потому, что от этих
 * величин ничего не перерисовывается: они отвечают на вопросы «какое окно
 * набрано», «докуда дочитано», «что придержано». Положи их в состояние стора —
 * и подписчики будут просыпаться на каждый чих.
 */
const desk = {
  /** Чей канал — приходит вместе со сводкой и живёт до перезагрузки. */
  serverOf: {} as Record<Uuid, Uuid | null>,
  /** До какого сообщения дочитано. Отсюда отметка знает, куда ей нельзя назад. */
  lastRead: {} as Record<Uuid, Uuid | null>,
  /** Отложенная сводка после удаления непрочитанного: пачку удалений склеивает в один запрос. */
  unreadAfterDelete: 0,
  /** С какого сообщения начинается непрочитанное. По нему встаёт черта. */
  firstUnread: {} as Record<Uuid, Uuid | null>,
  /**
   * Каналы, где счёт непрочитанного уткнулся в потолок.
   *
   * Число там значит «столько и больше», и вычитать из него можно ровно до
   * потолка: дальше оно перестаёт быть даже нижней оценкой, и правду надо
   * спрашивать заново.
   */
  capped: new Set<Uuid>(),
  /**
   * Каналы, отмеченные непрочитанными вручную. Пока человек в таком канале,
   * взгляд на ленту отметку не двигает — иначе она съехала бы обратно в ту
   * же секунду: сообщение-то на экране. Уходит из канала — отметка снова
   * живёт как обычно.
   */
  keptUnread: new Set<Uuid>(),
  /**
   * Сколько непрочитанного было в каждой ветке, когда мы её видели в
   * последний раз.
   *
   * Нужно, чтобы событие ветки могло поправить счётчик канала: само по себе
   * оно говорит «в этой ветке столько-то нового», а счётчик считает ветки, а
   * не ответы. Без памяти о прошлом значении второй ответ в той же ветке
   * прибавил бы к счётчику ещё единицу.
   */
  threadSeen: {} as Record<Uuid, number>,
  /**
   * Сколько упоминаний ветки сидит в счётчике её канала.
   *
   * Сводка складывает упоминания канала и его веток в одно число. Чтобы
   * прочитанная ветка забрала из него ровно своё, а «дочитал канал» не
   * погасил чужое, нужно помнить, чья тут доля.
   */
  threadNamed: {} as Record<Uuid, { channel: Uuid; count: number }>,
  /** Каналы, у которых история дочитана до начала: чтобы не долбить сервер. */
  exhausted: new Set<Uuid>(),
  /**
   * Каналы, чьё окно доходит до самого свежего сообщения.
   *
   * Канал с непрочитанным открывается на черте, а не в конце: под ней лежит не
   * вся оставшаяся лента, а первая её страница. Пока окно до конца не дошло,
   * приходящее живьём в него класть нельзя — в ленте получилась бы дыра.
   */
  latest: new Set<Uuid>(),
  /**
   * Пришедшее живьём в канал, чьё окно не доходит до конца.
   *
   * Выбросить нельзя: страницу вниз мы могли запросить раньше, чем сообщение
   * появилось, и тогда оно не придёт ни оттуда, ни отсюда. Ждёт здесь, пока
   * окно доберётся до конца, и вливается в ленту целиком.
   */
  held: {} as Record<Uuid, Message[]>,
  /**
   * Канал, окно которого только что бросили в конец.
   *
   * Ленте это надо знать, чтобы встать внизу, а не на черте: человек попросил
   * конец — кнопкой или тем, что написал сам. Вывести это из «окно теперь
   * доходит до конца» нельзя: обычное открытие канала проходит через ровно то
   * же самое, и лента переставала вставать на черту.
   */
  jumped: null as Uuid | null,
  /**
   * К какому сообщению лента должна подкрутиться, когда окно приедет.
   *
   * Отдельно от `jumped`: тот говорит «встань в конец», а этот — «встань вот
   * на это». Свести их в одно значило бы отвечать концом ленты на просьбу
   * показать цитату двухлетней давности.
   */
  jumpTo: {} as Record<Uuid, Uuid | undefined>,
  /**
   * Какое по счёту окно канала набрано сейчас.
   *
   * Страницу вверх или вниз просят у сервера для **того** окна, что было на
   * экране в момент запроса. Пока ответ едет, окно могут набрать заново — и
   * тогда пришедшее относится к чужой ленте: подмешивать его в новую нельзя, а
   * его «дальше ничего нет» вообще про другой хвост. Такой ответ выбрасывается
   * по несовпадению счёта.
   */
  era: {} as Record<Uuid, number>,
  /**
   * Какому окну отвечает лента, лежащая в состоянии.
   *
   * Пока новое окно едет, на экране прежнее — и достраивать его от края уже
   * нельзя: курсор взялся бы из чужой ленты, а короткий ответ на такой курсор
   * читается как «дальше ничего нет».
   */
  built: {} as Record<Uuid, number>,
  /** Какой канал человек сейчас читает. `null` — никакой. */
  reading: null as Uuid | null,
  /** Видно ли конец ленты открытого канала. */
  atEnd: true,
};

function clearDesk() {
  desk.serverOf = {};
  desk.lastRead = {};
  desk.firstUnread = {};
  desk.capped = new Set();
  desk.keptUnread = new Set();
  desk.threadSeen = {};
  desk.threadNamed = {};
  desk.exhausted = new Set();
  desk.latest = new Set();
  desk.held = {};
  desk.jumped = null;
  desk.jumpTo = {};
  desk.era = {};
  desk.built = {};
  desk.reading = null;
  desk.atEnd = true;
}

/**
 * Сумма по серверам: счётчик канала попадает своему серверу.
 *
 * Прежде считалось при сборке общего значения, то есть при отрисовке. Стор
 * отдаёт состояние как есть, поэтому сумма стала частью состояния и
 * пересчитывается там же, где меняется слагаемое.
 */
function rollUp(byChannel: Record<Uuid, number>): Record<Uuid, number> {
  return Object.entries(byChannel).reduce<Record<Uuid, number>>((sum, [channelId, count]) => {
    const server = desk.serverOf[channelId];
    if (!server || count <= 0) return sum;
    sum[server] = (sum[server] ?? 0) + count;
    return sum;
  }, {});
}

/** Пустое состояние: с него начинаем и к нему возвращаемся при выходе. */
function empty() {
  return {
    unread: {} as Record<Uuid, number>,
    mentions: {} as Record<Uuid, number>,
    serverUnread: {} as Record<Uuid, number>,
    serverMentions: {} as Record<Uuid, number>,
    notifyChannel: {} as Record<Uuid, NotifyLevel>,
    notifyUntil: {} as Record<Uuid, string>,
    notifyServer: {} as Record<Uuid, NotifyLevel>,
    pinnedChannels: {} as Record<Uuid, true>,
    since: {} as Record<Uuid, Uuid | undefined>,
    feeds: {} as Record<Uuid, Message[] | undefined>,
    queue: [] as Pending[],
    /*
     * Кто печатает: канал → человек → до какого мига держать надпись.
     *
     * Сроком, а не признаком: «перестал печатать» никто не присылает, и ждать
     * такого события значило бы держать надпись вечно, если человек закрыл
     * вкладку. Срок продлевается каждым новым событием.
     */
    typing: {} as Record<Uuid, Record<Uuid, number>>,
    replies: {} as Record<Uuid, Message | undefined>,
    panel: null as ThreadPanelState | null,
    threads: {} as Record<Uuid, Thread>,
    threadFeeds: {} as Record<Uuid, Message[]>,
    threadLists: {} as Record<Uuid, ThreadListItem[]>,
    threadFilter: true,
    threadUnread: {} as Record<Uuid, number>,
  };
}

type Updater<T> = T | ((previous: T) => T);

function apply<T>(previous: T, next: Updater<T>): T {
  return typeof next === "function" ? (next as (p: T) => T)(previous) : next;
}

/**
 * Сколько держится надпись без нового события.
 *
 * Больше, чем период повтора у того, кто печатает: иначе надпись мигала бы в
 * промежутках между его запросами.
 */
const TYPING_TTL = 7000;

/**
 * Отложенные перезапросы дерева каналов: сервер → счётчик.
 *
 * Перестановка каналов — это не одна правка, а по правке на каждый сдвинутый:
 * десяток запросов подряд, и на каждый прилетает событие. Без склейки дерево
 * перечитывалось бы десять раз, и промежуточные ответы ложились бы поверх
 * поздних — порядок на глазах прыгал бы туда-сюда.
 */
const treeSoon = new Map<Uuid, ReturnType<typeof setTimeout>>();

function forgetTreeReloads() {
  for (const timer of treeSoon.values()) clearTimeout(timer);
  treeSoon.clear();
}

/**
 * Запустить и забыть — но не молча.
 *
 * `void обещание` означает «ответ мне не нужен», а получается «отказ мне не
 * нужен»: сорвавшаяся загрузка улетает в необработанное отклонение. Ловит его
 * потом окно, и в журнале это выглядит поломкой приложения, хотя случилась
 * обычная вещь — у человека отняли право на канал, пока он в нём стоял.
 *
 * Отсюда и узость: это для загрузок, чей отказ ничего не ломает. Экран без
 * них покажет то же, что показывал, а следующая попытка придёт с событием
 * или с открытием. Всё, чего им не хватало, — строчки в журнале вместо крика.
 */
function detached(what: string, work: Promise<unknown>) {
  void work.catch((error: unknown) => {
    warn("лента", `не удалось: ${what}`, {
      причина: error instanceof Error ? error.message : String(error),
    });
  });
}

/*
 * Список постов форума — заново. Листавший дальше первой страницы получает
 * только отметку «устарело»: перечитывать все страницы на каждое сообщение
 * в форуме — по запросу на страницу, — дорого и бессмысленно.
 */
function refreshPosts(channelId: Uuid) {
  // Списков у форума по одному на сортировку — ключ с ней на конце.
  const deep = queryClient
    .getQueriesData<{ pages: unknown[] }>({ queryKey: keys.posts(channelId) })
    .some(([, cached]) => (cached?.pages.length ?? 0) > 1);
  void queryClient.invalidateQueries({
    queryKey: keys.posts(channelId),
    refetchType: deep ? "none" : "active",
  });
}

function reloadTreeSoon(serverId: Uuid) {
  /*
   * Незагруженный сервер не трогаем: он подтянется целиком, когда его
   * откроют. Иначе каждая чужая перестановка стоила бы запроса по каждому
   * серверу, где мы состоим, — даже по тем, куда не заходили.
   */
  if (!useCatalogue.getState().states[serverId]) return;

  const known = treeSoon.get(serverId);
  if (known) clearTimeout(known);

  treeSoon.set(
    serverId,
    setTimeout(() => {
      treeSoon.delete(serverId);
      detached("перечитать дерево сервера", useCatalogue.getState().loadServer(serverId));
    }, 150),
  );
}

/**
 * Уборка просроченных.
 *
 * Одна на все каналы и заводится только когда есть что убирать: держать
 * тикающий счётчик всё время сеанса ради надписи, которая появляется раз в
 * час, незачем.
 */
let sweeper: ReturnType<typeof setInterval> | null = null;

function stopSweeping() {
  if (sweeper === null) return;

  clearInterval(sweeper);
  sweeper = null;
}

function sweepTyping() {
  const now = Date.now();
  const current = useChat.getState().typing;

  let changed = false;
  const next: Record<Uuid, Record<Uuid, number>> = {};

  for (const [channelId, people] of Object.entries(current)) {
    const fresh: Record<Uuid, number> = {};

    for (const [userId, until] of Object.entries(people)) {
      if (until > now) fresh[userId] = until;
      else changed = true;
    }

    if (Object.keys(fresh).length > 0) next[channelId] = fresh;
  }

  if (changed) useChat.setState({ typing: next });
  if (Object.keys(next).length === 0) stopSweeping();
}

export const useChat = create<Chat>()((set, get) => {
  /*
   * Переходники под руку `useState`.
   *
   * Логика отметок и окон ленты слишком дорога, чтобы переписывать её ради
   * формы вызова, — тела ниже перенесены дословно. Эти обёртки дают им прежнюю
   * подпись поверх `set`.
   *
   * У счётчиков заодно пересчитываются суммы по серверам: раньше их считала
   * сборка общего значения, а стор отдаёт состояние как есть.
   */
  const setUnread = (next: Updater<Record<Uuid, number>>) =>
    set((state) => {
      const unread = apply(state.unread, next);
      return { unread, serverUnread: rollUp(unread) };
    });
  const setMentions = (next: Updater<Record<Uuid, number>>) =>
    set((state) => {
      const mentions = apply(state.mentions, next);
      return { mentions, serverMentions: rollUp(mentions) };
    });
  const setNotifyChannel = (next: Updater<Record<Uuid, NotifyLevel>>) =>
    set((state) => ({ notifyChannel: apply(state.notifyChannel, next) }));
  const setNotifyServer = (next: Updater<Record<Uuid, NotifyLevel>>) =>
    set((state) => ({ notifyServer: apply(state.notifyServer, next) }));
  const setSince = (next: Updater<Record<Uuid, Uuid | undefined>>) =>
    set((state) => ({ since: apply(state.since, next) }));
  const setFeeds = (next: Updater<Record<Uuid, Message[] | undefined>>) =>
    set((state) => ({ feeds: apply(state.feeds, next) }));
  const setQueue = (next: Updater<Pending[]>) =>
    set((state) => ({ queue: apply(state.queue, next) }));
  const setReplies = (next: Updater<Record<Uuid, Message | undefined>>) =>
    set((state) => ({ replies: apply(state.replies, next) }));
  const setPanel = (next: Updater<ThreadPanelState | null>) =>
    set((state) => ({ panel: apply(state.panel, next) }));
  const setThreads = (next: Updater<Record<Uuid, Thread>>) =>
    set((state) => ({ threads: apply(state.threads, next) }));
  const setThreadFeeds = (next: Updater<Record<Uuid, Message[]>>) =>
    set((state) => ({ threadFeeds: apply(state.threadFeeds, next) }));
  const setThreadLists = (next: Updater<Record<Uuid, ThreadListItem[]>>) =>
    set((state) => ({ threadLists: apply(state.threadLists, next) }));
  const setThreadFilterState = (next: Updater<boolean>) =>
    set((state) => ({ threadFilter: apply(state.threadFilter, next) }));
  const setThreadUnread = (next: Updater<Record<Uuid, number>>) =>
    set((state) => ({ threadUnread: apply(state.threadUnread, next) }));

  /**
   * Свои числа ветки — в счётчики её канала.
   *
   * Канал считает ветки с новым, а не ответы, и держит упоминания веток в
   * своей сумме. Правится он на разницу с тем, что ветка в него уже внесла:
   * второй ответ в ветке не прибавит лишнего, а прочитанная ветка заберёт
   * ровно своё. Раньше каждый ответ прибавлял к упоминаниям единицу, а
   * прочтение их не забирало — на значке «Почты» висело упоминание, которого
   * нигде нет.
   */
  const noteThread = (channelId: Uuid, threadId: Uuid, unread: number, mentions: number) => {
    const was = desk.threadSeen[threadId] ?? 0;
    const named = desk.threadNamed[threadId]?.count ?? 0;
    desk.threadSeen[threadId] = unread;
    desk.threadNamed[threadId] = { channel: channelId, count: mentions };

    if (was > 0 !== unread > 0) {
      setThreadUnread((previous) => ({
        ...previous,
        [channelId]: Math.max(0, (previous[channelId] ?? 0) + (unread > 0 ? 1 : -1)),
      }));
    }
    if (mentions !== named) {
      setMentions((previous) => ({
        ...previous,
        [channelId]: Math.max(0, (previous[channelId] ?? 0) + mentions - named),
      }));
    }
  };

  /** Упоминания канала, пришедшие из его веток: прочтение самого канала их не гасит. */
  const threadMentions = (channelId: Uuid) =>
    Object.values(desk.threadNamed).reduce(
      (sum, item) => (item.channel === channelId ? sum + item.count : sum),
      0,
    );

  /** Заводит новое окно канала и отдаёт его номер. */
  const renew = (channelId: Uuid) => {
    const next = (desk.era[channelId] ?? 0) + 1;
    desk.era[channelId] = next;
    return next;
  };

  /** Не сменилось ли окно, пока ехал ответ. */
  const current = (channelId: Uuid, mine: number) => (desk.era[channelId] ?? 0) === mine;

  /** Лежит ли на экране лента нынешнего окна, а не прежнего. */
  const settled = (channelId: Uuid) => (desk.built[channelId] ?? 0) === (desk.era[channelId] ?? 0);

  /*
   * «Заглушить на время»: когда ближайший срок выходит, выбор канала снимается
   * у себя — сервер к тому времени и сам перестанет его отдавать.
   */
  const unmuteTimer: { current: ReturnType<typeof setTimeout> | undefined } = { current: undefined };
  const scheduleUnmute = () => {
    clearTimeout(unmuteTimer.current);
    const entries = Object.entries(get().notifyUntil);
    if (entries.length === 0) return;
    const soonest = Math.min(...entries.map(([, until]) => Date.parse(until)));
    /*
     * Срок вышел — выбор перечитывается у сервера: он вернёт прежний свой
     * уровень канала, если тот был. По своим часам срок уже прошёл, а сервер
     * ещё глушит (часы расходятся) — следующая попытка через 15 секунд, а не
     * сразу: иначе это был бы частый опрос.
     */
    const left = soonest - Date.now();
    unmuteTimer.current = setTimeout(
      () => void reloadNotify().catch(() => scheduleUnmute()),
      // setTimeout дольше ~24,8 суток не умеет; срок короче недели, но страхуемся.
      left > 0 ? Math.min(left + 500, 2 ** 31 - 1) : 15_000,
    );
  };

  const reloadNotify = async () => {
    const chosen = await api.notify.choices();
    setNotifyChannel(
      Object.fromEntries(chosen.channels.map((item) => [item.channel_id, item.level])),
    );
    set({
      notifyUntil: Object.fromEntries(
        chosen.channels.flatMap((item) => (item.until ? [[item.channel_id, item.until] as const] : [])),
      ),
    });
    scheduleUnmute();
    setNotifyServer(
      Object.fromEntries(chosen.servers.map((item) => [item.server_id, item.level])),
    );
    set({ pinnedChannels: Object.fromEntries((chosen.pinned ?? []).map((id) => [id, true] as const)) });
  };

  /** Закрепить или открепить — сразу у себя, не ушло — обратно, как с окликом. */
  const setChannelPinned = async (channelId: Uuid, on: boolean) => {
    const mark = (value: boolean) =>
      set((state) => {
        const next = { ...state.pinnedChannels };
        if (value) next[channelId] = true;
        else delete next[channelId];
        return { pinnedChannels: next };
      });
    mark(on);
    try {
      await (on ? api.notify.pin(channelId) : api.notify.unpin(channelId));
    } catch (error) {
      mark(!on);
      throw error;
    }
  };

  /**
   * Выбор уровня. `null` — вернуть к умолчанию.
   *
   * Своё состояние правится сразу, не дожидаясь ответа: настройка тихая, и
   * подтверждать её галочкой через полсекунды незачем. Не ушло — вернём
   * обратно, чтобы на экране не осталось того, чего нет на сервере.
   */
  const setChannelNotify = async (channelId: Uuid, level: NotifyLevel | null, minutes: number | null = null) => {
    const was = get().notifyChannel[channelId];
    const wasUntil = get().notifyUntil[channelId];
    const markUntil = (until: string | undefined) =>
      set((state) => {
        const next = { ...state.notifyUntil };
        if (until) next[channelId] = until;
        else delete next[channelId];
        return { notifyUntil: next };
      });
    setNotifyChannel((previous) => chosen(previous, channelId, level));
    markUntil(minutes ? new Date(Date.now() + minutes * 60_000).toISOString() : undefined);
    scheduleUnmute();

    try {
      await api.notify.setChannel(channelId, level, minutes);
    } catch (error) {
      setNotifyChannel((previous) => chosen(previous, channelId, was ?? null));
      markUntil(wasUntil);
      scheduleUnmute();
      throw error;
    }
  };

  const unmuteChannel = async (channelId: Uuid) => {
    // Прежний уровень знает сервер: выключаем сразу, а что вернулось — перечитываем.
    const was = get().notifyChannel[channelId];
    setNotifyChannel((previous) => chosen(previous, channelId, null));
    try {
      await api.notify.unmuteChannel(channelId);
    } catch (error) {
      setNotifyChannel((previous) => chosen(previous, channelId, was ?? null));
      throw error;
    }
    await reloadNotify().catch(() => {});
  };

  const setServerNotify = async (serverId: Uuid, level: NotifyLevel | null) => {
    const was = get().notifyServer[serverId];
    setNotifyServer((previous) => chosen(previous, serverId, level));

    try {
      await api.notify.setServer(serverId, level);
    } catch (error) {
      setNotifyServer((previous) => chosen(previous, serverId, was ?? null));
      throw error;
    }
  };

  /** Уровень сервера: свой выбор, а без него — то, что сервер задал по умолчанию. */
  const serverLevel = (serverId: Uuid): NotifyLevel | undefined =>
    get().notifyServer[serverId] ??
    useCatalogue.getState().states[serverId]?.default_notify ??
    useCatalogue.getState().servers.find((item) => item.id === serverId)?.default_notify;

  const reloadUnread = async () => {
    const items = (await api.read.unread()).items;
    desk.serverOf = Object.fromEntries(items.map((item) => [item.channel_id, item.server_id]));
    desk.lastRead = Object.fromEntries(items.map((item) => [item.channel_id, item.last_read_id]));
    desk.firstUnread = Object.fromEntries(
      items.map((item) => [item.channel_id, item.first_unread_id]),
    );
    desk.capped = new Set(items.filter((item) => item.capped).map((item) => item.channel_id));
    setUnread(Object.fromEntries(items.map((item) => [item.channel_id, item.unread])));
    setMentions(Object.fromEntries(items.map((item) => [item.channel_id, item.mentions])));
    setThreadUnread(Object.fromEntries(items.map((item) => [item.channel_id, item.threads])));

    /*
     * Ветки сводка называет только суммой по каналу. Какие ветки в неё вошли и
     * с какими числами — спрашиваем у каналов, где сумма не ноль (их обычно
     * один-два): иначе следующий ответ в такой ветке прибавился бы к сумме
     * второй раз. В остальных каналах подписанные ветки прочитаны, и
     * незнакомая ветка там честно стоит на нуле.
     */
    desk.threadSeen = {};
    desk.threadNamed = {};
    const learning = Promise.all(
      items
        .filter((item) => item.threads > 0)
        .map((item) =>
          api.threads.list(item.channel_id, true).then(
            ({ items: threads }) => {
              for (const thread of threads) {
                // Событие, успевшее раньше списка, свежее его.
                desk.threadSeen[thread.id] ??= thread.unread;
                desk.threadNamed[thread.id] ??= { channel: item.channel_id, count: thread.mentions };
              }
            },
            () => {},
          ),
        ),
    );

    /*
     * Сводка приходит своим ходом и могла опоздать к открытию канала: тогда
     * окно набрано «с конца» и границы в нём нет. Набираем заново — иначе
     * отметка внутри окна погасит всё, что осталось выше него.
     */
    const open = desk.reading;
    if (open) {
      const from = desk.firstUnread[open];
      const feed = get().feeds[open];
      if (from && feed && !feed.some((item) => item.id === from)) await loadMessages(open);
    }
    await learning;
  };

  /*
   * Отметка идёт по последнему известному сообщению канала, а не «по времени»:
   * сервер хранит именно сообщение, и время у клиента для этого не годится.
   *
   * Счётчик гасится сразу, не дожидаясь ответа: засечка — про внимание
   * человека, и держать её лишнюю секунду после того, как он уже читает,
   * значит врать.
   */
  const markRead = (channelId: Uuid) => {
    /*
     * Последнее сообщение спрашиваем у сервера, а не берём из ленты.
     *
     * Окно кончается самым свежим не всегда: канал с непрочитанным
     * открывается на черте, и внизу у него середина разговора. Отметить по
     * ней «прочитано целиком» значило бы обещать больше, чем сделано, —
     * счётчик бы обнулился, а непрочитанное осталось.
     */
    void (async () => {
      const [last] = await api.messages.list(channelId, { limit: 1 });
      if (!last) return;

      await api.read.mark(channelId, last.id);
      desk.keptUnread.delete(channelId);
      desk.lastRead[channelId] = last.id;
      desk.firstUnread[channelId] = null;
      setUnread((previous) => (previous[channelId] ? { ...previous, [channelId]: 0 } : previous));
      // Упоминания в ветках остаются: ветки читают отдельно.
      setMentions((previous) =>
        previous[channelId] ? { ...previous, [channelId]: threadMentions(channelId) } : previous,
      );
    })().catch(() => {});
  };

  const markUnread = (channelId: Uuid, messageId: Uuid) => {
    void (async () => {
      const summary = await api.read.unmark(channelId, messageId);
      desk.keptUnread.add(channelId);
      desk.lastRead[channelId] = summary.last_read_id;
      desk.firstUnread[channelId] = summary.first_unread_id;
      if (summary.capped) desk.capped.add(channelId);
      else desk.capped.delete(channelId);
      setUnread((previous) => ({ ...previous, [channelId]: summary.unread }));
      setMentions((previous) => ({ ...previous, [channelId]: summary.mentions }));
      // Черта — над этим сообщением и сразу: человек смотрит ровно сюда.
      setSince((previous) => ({ ...previous, [channelId]: summary.first_unread_id ?? undefined }));
    })().catch(() => {});
  };

  const releaseKept = (channelId: Uuid) => {
    desk.keptUnread.delete(channelId);
  };

  const setReading = (channelId: Uuid | null) => {
    const left = desk.reading;
    desk.reading = channelId;

    // Ушли из канала — черта забывается: в следующий раз она встанет заново
    // по тому, что накопилось за это время.
    /*
     * Отмеченное непрочитанным держится, пока не ушли в другой канал. Уход
     * фокуса из окна — не уход из канала: вернулся — отметка на месте.
     */
    if (channelId) {
      for (const kept of desk.keptUnread) if (kept !== channelId) desk.keptUnread.delete(kept);
    }

    if (left && left !== channelId) {
      setSince((previous) => {
        if (!(left in previous)) return previous;
        const next = { ...previous };
        delete next[left];
        return next;
      });
    }

    if (!channelId) return;

    /*
     * Границу снимаем **сейчас**, а не внутри обновления состояния.
     *
     * Обновляющая функция вызывается не в этот миг, а при следующей
     * отрисовке — а к тому времени отметка ниже уже сдвинет границу на
     * последнее сообщение. Если лента канала уже в памяти, отметка проходит
     * мгновенно, и черта вставала ровно под последним сообщением: то есть
     * там, где от неё нет никакого толку.
     *
     * Пока сводка не пришла, границы нет и выдумывать её нельзя: «всё ново»
     * поставило бы черту в начало годовой истории.
     */
    const waiting = (get().unread[channelId] ?? 0) > 0;
    const frozen = waiting ? (desk.firstUnread[channelId] ?? undefined) : undefined;

    /*
     * Замер состоялся — но только если он отвечает на вопрос.
     *
     * «Непрочитанное есть, а где начинается — неизвестно» замером не
     * считается: замороженная в этом виде черта означала бы «читать можно
     * откуда угодно», и первая же строка в поле зрения записала бы в
     * прочитанное всю историю выше окна. Такое бывает, когда сводка пришла
     * от узла постарше и поля с границей в ней нет вовсе.
     */
    const known = waiting ? Boolean(frozen) : channelId in desk.firstUnread;

    /*
     * Канал с непрочитанным откроется на черте, а не в конце ленты. Значит
     * конца человек пока не видел, и дочитывать за него нечего — отметка
     * встанет, когда он долистает.
     */
    desk.atEnd = !waiting;

    if (known) {
      setSince((previous) =>
        channelId in previous ? previous : { ...previous, [channelId]: frozen },
      );
    }
  };

  /**
   * Двигает отметку до увиденного сообщения.
   *
   * Счётчик уменьшается ровно на то, что человек между делом прочёл: считать
   * заново по всей ленте нельзя — начало истории могло и не загрузиться.
   */
  const saw = (channelId: Uuid, messageId: Uuid) => {
    /*
     * Считается увиденным только то, что показалось человеку, а не окну.
     *
     * Свёрнутое или неактивное окно продолжает держать ленту на месте, и
     * наблюдатель исправно докладывает о видимых строках. Читает их при этом
     * никто: канал перестаёт быть читаемым, как только уходит фокус.
     */
    if (desk.reading !== channelId) return;
    if (desk.keptUnread.has(channelId)) return;

    /*
     * Пока граница не замерена, отметку двигать нельзя.
     *
     * Сводка непрочитанного приходит позже истории, и в это окно лента ещё
     * стоит внизу, на самых свежих сообщениях. Засчитать их там значило бы
     * прочитать за человека всю сотню, которую он не видел, — отметка одна на
     * канал и тянет за собой всё, что выше.
     */
    if (!(channelId in get().since)) return;

    const feed = get().feeds[channelId];
    if (!feed) return;

    /*
     * Граница должна лежать внутри окна, иначе отметка не про то.
     *
     * Она одна на канал и тянет за собой всё, что выше: поставить её на
     * увиденное, не имея под рукой начала непрочитанного, — значит записать в
     * прочитанное и всю ту сотню, что осталась за верхним краем. Окно набирают
     * от границы (см. `loadMessages`), но у совсем запущенного канала запаса
     * может и не хватить — тогда отметка ждёт, пока человек долистает вверх.
     */
    const from = get().since[channelId];
    if (from !== undefined && !feed.some((item) => item.id === from)) return;

    const seen = feed.findIndex((item) => item.id === messageId);
    if (seen < 0) return;

    const marked = desk.lastRead[channelId];
    const at = marked ? feed.findIndex((item) => item.id === marked) : -1;

    // Назад отметка не едет: взгляд, скользнувший вверх по истории, не должен
    // возвращать уже прочитанное.
    if (at >= seen) return;

    desk.lastRead[channelId] = messageId;
    void api.read.mark(channelId, messageId).catch(() => {});

    /*
     * Остаток уменьшается на прочтённое, а не считается по ленте заново.
     *
     * Пересчёт по окну был точен, пока окно кончалось самым свежим сообщением.
     * Теперь канал открывается на черте, и под нижним краем окна лежит ещё не
     * загруженное: «всё, что ниже отметки в ленте» — это уже не весь остаток,
     * а только его начало.
     *
     * Вычитается то, что прошло между старой отметкой и новой, не своё и не
     * выше черты: выше неё прочитанное и так, второй раз его не считают.
     */
    const me = useSession.getState().me;
    const border = from ? feed.findIndex((item) => item.id === from) : 0;
    const read = feed
      .slice(Math.max(at + 1, border), seen + 1)
      .filter((item) => item.author.id !== me?.id);
    const named = read.filter((item) => item.content.includes(`@${me?.handle ?? ""}`)).length;

    /*
     * Дочитали до конца загруженного, и загруженное доходит до конца ленты —
     * непрочитанного не осталось. Это единственное место, где счёт снова
     * известен точно, а не по вычитанию: пусть оно и правит накопившееся.
     */
    const done = seen === feed.length - 1 && desk.latest.has(channelId);

    /*
     * Куда встанет черта, когда канал откроют в следующий раз. Замороженная на
     * сейчас (`since`) при этом не двигается: она под глазами.
     *
     * Своё сообщение границей не бывает — непрочитанным оно не считается. А
     * если следующего в окне вовсе нет и до конца ленты мы не дошли, границу
     * оставляем прежней: врать про неё хуже, чем знать устаревшее, и сводка
     * всё равно поправит.
     */
    const next = feed.slice(seen + 1).find((item) => item.author.id !== me?.id);
    if (done) desk.firstUnread[channelId] = null;
    else if (next) desk.firstUnread[channelId] = next.id;

    setUnread((previous) => ({
      ...previous,
      [channelId]: done ? 0 : Math.max(0, (previous[channelId] ?? 0) - read.length),
    }));
    // Упоминания из веток прочтение канала не гасит: их читают в самих ветках.
    const kept = threadMentions(channelId);
    setMentions((previous) => ({
      ...previous,
      [channelId]: done ? kept : Math.max(kept, (previous[channelId] ?? 0) - named),
    }));

    /*
     * Счёт упирался в потолок, а теперь ушёл ниже — вычитать дальше не из
     * чего: за потолком осталось неизвестно сколько. Спрашиваем сводку заново,
     * и она снова назовёт или точное число, или потолок.
     */
    if (desk.capped.has(channelId)) {
      const left = Math.max(0, (get().unread[channelId] ?? 0) - read.length);
      if (done || left < UNREAD_CAP) {
        desk.capped.delete(channelId);
        void reloadUnread();
      }
    }
  };

  const setAtEnd = (value: boolean) => {
    desk.atEnd = value;
  };

  /**
   * Вливает в ленту то, что пришло живьём, пока окно не доходило до конца.
   *
   * Вливает только когда окно до конца добралось: раньше эти сообщения
   * оставили бы в ленте дыру. Зовётся отовсюду, где окно меняется, — решение
   * принимается здесь, а не на каждой стороне вызова.
   */
  const release = (channelId: Uuid, items: Message[]): Message[] => {
    if (!desk.latest.has(channelId)) return items;

    const waiting = desk.held[channelId];
    if (!waiting?.length) return items;

    delete desk.held[channelId];
    return merge(items, waiting);
  };

  /**
   * Набирает окно канала.
   *
   * Есть непрочитанное — окно встаёт **вокруг черты**: половина прочитанного
   * над ней, первая страница нового под ней. Так человек попадает туда, где
   * остановился, а не в конец разговора, и отметка едет от границы подряд, ни
   * через что не перепрыгивая.
   *
   * Нет непрочитанного — окно берётся с конца: показывать начало годовой
   * истории там незачем.
   */
  const loadMessages = async (channelId: Uuid) => {
    const from = desk.firstUnread[channelId];

    // Окно набирается заново — и то, куда оно дотягивается, тоже. Отметки от
    // прошлого окна к новому не относятся.
    const mine = renew(channelId);
    desk.exhausted.delete(channelId);
    desk.latest.delete(channelId);

    if (!from) {
      const items = sortAsc(await api.messages.list(channelId, { limit: PAGE }));
      if (!current(channelId, mine)) return;

      if (items.length < PAGE) desk.exhausted.add(channelId);
      desk.latest.add(channelId);
      desk.built[channelId] = mine;
      setFeeds((previous) => ({ ...previous, [channelId]: release(channelId, items) }));
      return;
    }

    /*
     * Границы может уже не быть: сообщение на ней удалили, пока канал был
     * закрыт. Сервер об этом теперь говорит отказом, а не пустой страницей,
     * и правильный ответ здесь — забыть устаревшую границу и набрать окно с
     * конца. Сводка потом назовёт новую.
     */
    let items: Message[];
    try {
      items = sortAsc(await api.messages.list(channelId, { limit: OPEN, nearby: from }));
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) throw error;

      desk.firstUnread[channelId] = null;
      const tail = sortAsc(await api.messages.list(channelId, { limit: PAGE }));
      if (!current(channelId, mine)) return;

      if (tail.length < PAGE) desk.exhausted.add(channelId);
      desk.latest.add(channelId);
      desk.built[channelId] = mine;
      setFeeds((previous) => ({ ...previous, [channelId]: release(channelId, tail) }));
      return;
    }

    if (!current(channelId, mine)) return;

    /*
     * Дошли ли до краёв — считаем по самому окну, а не по его длине.
     *
     * Запрос вокруг сообщения набирает две половины, и короткой может
     * оказаться любая: сверху — если истории меньше, снизу — если непрочитанного
     * меньше половины. Общая длина об этом не говорит ничего.
     */
    const at = items.findIndex((item) => item.id === from);
    if (at >= 0) {
      if (at + 1 < OPEN / 2) desk.exhausted.add(channelId);
      if (items.length - at - 1 < OPEN / 2) desk.latest.add(channelId);
    }

    desk.built[channelId] = mine;
    setFeeds((previous) => ({ ...previous, [channelId]: release(channelId, items) }));
  };

  /**
   * Набирает окно вокруг названного сообщения.
   *
   * Тот же запрос, каким канал открывается на черте, и та же бухгалтерия
   * краёв: `nearby` набирает две половины, и короткой может оказаться любая.
   *
   * Сообщения может уже не быть — его удалили, пока цитата жила в чужой
   * ленте. Тогда не двигаем ничего: окно на месте, а прыжок в никуда хуже
   * бездействия.
   */
  const openAt = async (channelId: Uuid, messageId: Uuid) => {
    const mine = renew(channelId);

    const items = await api.messages
      .list(channelId, { limit: OPEN, nearby: messageId })
      .then(sortAsc)
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      });

    if (!current(channelId, mine)) return;

    /*
     * Сообщения нет — окно оставляем как есть. Но счёт окон уже сменился, и
     * без этой строки лента перестала бы догружаться вовсе: подгрузка молчит,
     * пока построенное не совпадает со счётом.
     */
    if (items === null) {
      desk.built[channelId] = mine;
      return;
    }

    desk.exhausted.delete(channelId);
    desk.latest.delete(channelId);

    const at = items.findIndex((item) => item.id === messageId);
    if (at >= 0) {
      if (at + 1 < OPEN / 2) desk.exhausted.add(channelId);
      if (items.length - at - 1 < OPEN / 2) desk.latest.add(channelId);
    }

    desk.built[channelId] = mine;
    desk.jumpTo[channelId] = messageId;
    setFeeds((previous) => ({ ...previous, [channelId]: release(channelId, items) }));
  };

  /**
   * Догружает страницу вниз, к более свежему.
   *
   * Зеркало подгрузки вверх: канал с непрочитанным открывается на черте, и
   * дальше лента растёт в ту сторону, в которую её читают.
   */
  const loadNewer = async (channelId: Uuid) => {
    if (desk.latest.has(channelId)) return false;
    // Лента на экране от прежнего окна — край у неё чужой, и достраивать по
    // нему нечего.
    if (!settled(channelId)) return false;

    const known = get().feeds[channelId];
    if (!known?.length) return false;

    const mine = desk.era[channelId] ?? 0;
    const items = await api.messages
      .list(channelId, { limit: PAGE, after: known[known.length - 1].id })
      // Нижнее сообщение окна удалили — листать от него нечем.
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      });

    // Окно сменилось, пока ехал ответ: он про чужой хвост, и «дальше ничего
    // нет» здесь означало бы совсем не то.
    if (!current(channelId, mine)) return false;
    if (items === null) {
      detached("набрать окно заново", loadMessages(channelId));
      return false;
    }

    if (items.length < PAGE) desk.latest.add(channelId);
    if (!items.length) {
      setFeeds((previous) => ({
        ...previous,
        [channelId]: release(channelId, previous[channelId] ?? []),
      }));
      return false;
    }

    setFeeds((previous) => {
      const grown = release(channelId, merge(previous[channelId] ?? [], items));
      if (grown.length <= KEEP) return { ...previous, [channelId]: grown };

      /*
       * Отрезаем старое сверху. Раз отрезали — выше снова есть что грузить,
       * даже если только что выяснилось, что там начало разговора.
       */
      desk.exhausted.delete(channelId);
      return { ...previous, [channelId]: grown.slice(-KEEP) };
    });
    return true;
  };

  /**
   * Бросает окно в конец ленты.
   *
   * Нужно там, где ждать прокрутки нельзя: человек написал сам — своё
   * сообщение он должен увидеть, — или нажал «к последним».
   */
  const jumpToLatest = async (channelId: Uuid) => {
    if (desk.latest.has(channelId)) return;

    // Окно меняется целиком — значит новое, и всё, что едет к прежнему,
    // больше не в счёт.
    const mine = renew(channelId);
    const items = sortAsc(await api.messages.list(channelId, { limit: PAGE }));
    if (!current(channelId, mine)) return;

    desk.latest.add(channelId);
    desk.jumped = channelId;
    // Окно поехало в конец — что было выше, в него больше не входит, и
    // историю придётся набирать заново по мере подъёма.
    desk.exhausted.delete(channelId);
    desk.built[channelId] = mine;
    setFeeds((previous) => ({ ...previous, [channelId]: release(channelId, items) }));
  };

  const loadOlder = async (channelId: Uuid) => {
    if (desk.exhausted.has(channelId)) return false;
    if (!settled(channelId)) return false;

    const known = get().feeds[channelId];
    if (!known?.length) return false;

    const mine = desk.era[channelId] ?? 0;
    // Верхнее сообщение окна могли удалить — тогда листать от него нечем, и
    // набрать окно заново честнее, чем молча остановиться.
    const items = await api.messages
      .list(channelId, { limit: PAGE, before: known[0].id })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      });

    if (!current(channelId, mine)) return false;
    if (items === null) {
      detached("набрать окно заново", loadMessages(channelId));
      return false;
    }

    if (items.length < PAGE) desk.exhausted.add(channelId);
    if (!items.length) return false;

    setFeeds((previous) => {
      const grown = merge(previous[channelId] ?? [], items);
      if (grown.length <= KEEP) return { ...previous, [channelId]: grown };

      /*
       * Отрезаем свежее снизу — и тем самым перестаём примыкать к концу
       * разговора. Признак снимается не для порядка: по нему живые сообщения
       * решают, влиться в ленту или подождать в стороне (`release`), а
       * влившись в окно с отрезанным низом, они оставили бы в нём дыру.
       */
      desk.latest.delete(channelId);
      return { ...previous, [channelId]: grown.slice(0, KEEP) };
    });
    return true;
  };

  /**
   * Правит сообщение там, где оно лежит.
   *
   * Мест два: лента канала и ответы открытой ветки. Ответ ветки в ленту не
   * попадает вовсе, и писать только в неё значит не написать никуда — значок
   * появлялся в ветке лишь после переоткрытия панели, потому что панель
   * перечитывает ветку с нуля. Кто зовёт, про это знать не должен: реакция
   * одинакова и в ленте, и в панели.
   *
   * Ветки перебираются все: их открыто не больше одной, а искать нужную по
   * сообщению значило бы держать вторую карту ради того же ответа.
   */
  const patchMessage = (channelId: Uuid, messageId: Uuid, change: (message: Message) => Message) => {
    setFeeds((previous) => {
      const known = previous[channelId];
      if (!known?.some((item) => item.id === messageId)) return previous;

      return {
        ...previous,
        [channelId]: known.map((item) => (item.id === messageId ? change(item) : item)),
      };
    });

    setThreadFeeds((previous) => {
      let next = previous;

      for (const [threadId, replies] of Object.entries(previous)) {
        if (!replies.some((item) => item.id === messageId)) continue;
        if (next === previous) next = { ...previous };
        next[threadId] = replies.map((item) => (item.id === messageId ? change(item) : item));
      }

      return next;
    });
  };

  const put = (message: Message) => {
    /*
     * Окно не доходит до конца — сообщению в ленте не место: между ним и
     * последней загруженной строкой лежит непрочитанное, которого в окне нет,
     * и вставка сделала бы из ленты обрывок, выглядящий целым. Ждёт в стороне,
     * пока лента до конца дочитается.
     */
    if (get().feeds[message.channel_id] && !desk.latest.has(message.channel_id)) {
      const waiting = (desk.held[message.channel_id] ??= []);
      if (!waiting.some((item) => item.id === message.id)) waiting.push(message);
      return;
    }

    setFeeds((previous) => {
      const known = previous[message.channel_id];
      // Канал не открыт — складывать сообщение некуда, и не нужно: при
      // открытии история придёт целиком.
      if (!known) return previous;

      /*
       * Реакции остаются свои. Событие о правке приходит без них — сервер их
       * не рассылает, потому что «моя реакция» у каждого своя, — и взять
       * оттуда пустой список значило бы стереть значки у всех, кто в это время
       * смотрел на сообщение.
       */
      const before = known.find((item) => item.id === message.id);
      // Свой голос в опросе — тоже свой: событие о правке несёт его пустым.
      const poll =
        before?.poll && message.poll ? { ...message.poll, mine: before.poll.mine } : message.poll;
      const incoming = before ? { ...message, reactions: before.reactions, poll } : message;

      return { ...previous, [message.channel_id]: merge(known, [incoming]) };
    });
  };

  const send = (
    channelId: Uuid,
    content: string,
    attachmentIds: Uuid[],
    replyToId: Uuid | null = null,
    mentionAuthor = true,
    spoilerIds: Uuid[] = [],
    descriptions: { id: Uuid; text: string }[] = [],
  ) => {
    // Намеренно не `async`: функция либо синхронно отказывает, либо кладёт
    // сообщение в локальную очередь до возврата Promise. Поле ввода очищается
    // лишь после этого и не попадает в гонку с передачей управления updater.
    assertCanStartRestartSensitiveOperation();
    const key = `${channelId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    setQueue((previous) => [
      ...previous,
      { key, channelId, content, failed: false, attachmentIds, replyToId, mentionAuthor, spoilerIds, descriptions },
    ]);

    return (async () => {
      try {
        const quiet = silentPrefix(content);
        const sent = await api.messages.post(
          channelId,
          quiet.content,
          attachmentIds,
          replyToId,
          mentionAuthor,
          null,
          null,
          quiet.silent,
          null,
          [],
          spoilerIds,
          null,
          descriptions,
        );
        // Своё написанное человек должен увидеть — даже если читал середину
        // истории. Значит окно едет в конец, а не сообщение в середину.
        if (!desk.latest.has(channelId)) await jumpToLatest(channelId);
        else put(sent);
        setQueue((previous) => previous.filter((item) => item.key !== key));
      } catch (error) {
        // Не ушло — остаётся в очереди с пометкой, причиной и кнопкой повтора.
        const reason = refusal(error);
        setQueue((previous) =>
          previous.map((item) => (item.key === key ? { ...item, failed: true, reason } : item)),
        );
      }
    })();
  };

  const sendSticker = async (channelId: Uuid, stickerId: Uuid) => {
    assertCanStartRestartSensitiveOperation();
    const sent = await api.messages.post(channelId, "", [], null, true, stickerId);
    if (!desk.latest.has(channelId)) await jumpToLatest(channelId);
    else put(sent);
  };

  const sendVoice = async (channelId: Uuid, take: VoiceTake, replyToId: Uuid | null = null) => {
    assertCanStartRestartSensitiveOperation();
    const file = await api.files.upload(take.file, "attachment");
    const sent = await api.messages.post(channelId, "", [file.id], replyToId, true, null, null, false, null, [], [], {
      duration_ms: take.durationMs,
      waveform: take.waveform,
    });
    if (!desk.latest.has(channelId)) await jumpToLatest(channelId);
    else put(sent);
  };

  const sendPost = async (channelId: Uuid, title: string, content: string, tagIds: Uuid[] = []) => {
    assertCanStartRestartSensitiveOperation();
    const quiet = silentPrefix(content);
    await api.messages.post(channelId, quiet.content, [], null, true, null, null, quiet.silent, title, tagIds);
    await queryClient.invalidateQueries({ queryKey: keys.posts(channelId) });
  };

  const sendPoll = async (channelId: Uuid, poll: PollDraft) => {
    assertCanStartRestartSensitiveOperation();
    const sent = await api.messages.post(channelId, "", [], null, true, null, poll);
    if (!desk.latest.has(channelId)) await jumpToLatest(channelId);
    else put(sent);
  };

  const endPoll = async (channelId: Uuid, messageId: Uuid) => {
    const poll = await api.messages.endPoll(channelId, messageId);
    patchMessage(channelId, messageId, (item) => ({ ...item, poll }));
  };

  const vote = async (channelId: Uuid, messageId: Uuid, answerIds: Uuid[]) => {
    const poll = await api.messages.vote(channelId, messageId, answerIds);
    patchMessage(channelId, messageId, (item) => ({ ...item, poll }));
  };

  const retry = async (key: string) => {
    try {
      assertCanStartRestartSensitiveOperation();
    } catch {
      return;
    }
    const item = get().queue.find((entry) => entry.key === key);
    if (!item) return;

    setQueue((previous) => previous.map((entry) => (entry.key === key ? { ...entry, failed: false } : entry)));

    try {
      const quiet = silentPrefix(item.content);
      const sent = await api.messages.post(
        item.channelId,
        quiet.content,
        item.attachmentIds,
        item.replyToId,
        item.mentionAuthor,
        null,
        null,
        quiet.silent,
        null,
        [],
        item.spoilerIds ?? [],
        null,
        item.descriptions ?? [],
      );
      if (!desk.latest.has(item.channelId)) await jumpToLatest(item.channelId);
      else put(sent);
      setQueue((previous) => previous.filter((entry) => entry.key !== key));
    } catch (error) {
      const reason = refusal(error);
      setQueue((previous) =>
        previous.map((entry) => (entry.key === key ? { ...entry, failed: true, reason } : entry)),
      );
    }
  };

  const drop = (key: string) => {
    setQueue((previous) => previous.filter((entry) => entry.key !== key));
  };

  const toggleReaction = async (channelId: Uuid, messageId: Uuid, emoji: string, mine: boolean) => {
    const shift = (added: boolean) =>
      patchMessage(channelId, messageId, (item) => withReaction(item, emoji, added, true));

    shift(!mine);

    try {
      if (mine) await api.messages.unreact(channelId, messageId, emoji);
      else await api.messages.react(channelId, messageId, emoji);
    } catch {
      // Не приняли — возвращаем как было. Молча: причина видна по тому, что
      // значок отскочил обратно, а лишнее окно поверх ленты тут ни к чему.
      shift(mine);
    }
  };

  /** Подтягивает ветку и её ответы. */
  const loadThread = async (channelId: Uuid, threadId: Uuid) => {
    const [info, replies] = await Promise.all([
      api.threads.get(channelId, threadId),
      api.threads.messages(channelId, threadId, { limit: PAGE }),
    ]);

    setThreads((previous) => ({ ...previous, [threadId]: info }));
    setThreadFeeds((previous) => ({ ...previous, [threadId]: replies }));
    // Свежие числа правят и канал: ветку могли прочитать на другом устройстве, а об этом событие не приходит.
    noteThread(channelId, threadId, info.unread, info.mentions);
  };

  const loadThreadList = async (channelId: Uuid, followed: boolean) => {
    const items = (await api.threads.list(channelId, followed)).items;

    setThreadLists((previous) => ({ ...previous, [channelId]: items }));
    for (const item of items) noteThread(channelId, item.id, item.unread, item.mentions);
  };

  const openThread = (channelId: Uuid, rootId: Uuid, threadId: Uuid | null, full = false) => {
    setPanel({ channelId, kind: "thread", rootId, threadId, full });
    if (threadId) detached("открыть ветку", loadThread(channelId, threadId));
  };

  const expandThread = (full: boolean) =>
    setPanel((previous) => (previous?.kind === "thread" ? { ...previous, full } : previous));

  const reloadThread = (channelId: Uuid, threadId: Uuid) => {
    if (get().threads[threadId]) detached("перечитать ветку", loadThread(channelId, threadId));
  };

  const openThreadList = (channelId: Uuid) => {
    setPanel({ channelId, kind: "list" });
    detached("список веток", loadThreadList(channelId, get().threadFilter));
  };

  const closePanel = () => setPanel(null);

  /**
   * Ответ в открытой ветке. Первый ответ её и заводит: до него ветки нет ни
   * на сервере, ни у кого, кроме нас.
   */
  const sendInThread = async (
    content: string,
    attachmentIds: Uuid[],
    title?: string | null,
    extra: ThreadExtra = {},
  ) => {
    assertCanStartRestartSensitiveOperation();
    const panel = get().panel;
    if (!panel || panel.kind !== "thread") return;

    const { channelId, rootId, threadId } = panel;

    // Голосовое — как в канале (`sendVoice`): сначала запись файлом, потом сообщение с ней одной.
    const files = extra.voice ? [(await api.files.upload(extra.voice.file, "attachment")).id] : attachmentIds;
    const quiet = silentPrefix(content);
    const body = {
      spoiler_ids: extra.spoilerIds,
      descriptions: extra.descriptions,
      voice: extra.voice ? { duration_ms: extra.voice.durationMs, waveform: extra.voice.waveform } : undefined,
      sticker_id: extra.stickerId,
      silent: quiet.silent,
    };

    const sent = threadId
      ? await api.threads.reply(channelId, threadId, quiet.content, files, body)
      : await api.threads.replyToRoot(channelId, rootId!, quiet.content, files, body);

    const id = sent.thread_id!;

    // Ветка только что завелась — панель узнаёт её номер и грузит целиком.
    if (!threadId) {
      // Имя с шага «Новая ветка» — до загрузки, чтобы ветка пришла уже названной. Не встало — ветка всё равно есть.
      if (title) await api.threads.title(channelId, id, title).catch(() => {});
      setPanel((previous) =>
        previous && previous.kind === "thread" && previous.rootId === rootId
          ? { ...previous, threadId: id }
          : previous,
      );
      await loadThread(channelId, id);
    } else {
      setThreadFeeds((previous) => ({
        ...previous,
        [id]: merge(previous[id] ?? [], [sent]),
      }));
    }

    /*
     * Строка ветки в ленте канала обновляется отсюда же: своё событие до нас
     * не доходит — исходное соединение из рассылки исключено, — а строка
     * должна вырасти сразу. Ответивший теперь следит за веткой — он и в её
     * составе.
     */
    await refreshThreadRow(channelId, sent.thread_id!);
    void queryClient.invalidateQueries({ queryKey: keys.threadMembers(id) });
  };

  /**
   * Пересобирает строку ветки под корнем в ленте канала — и счёт открытой
   * ветки: своё событие `thread` до нас не доходит, и после своего ответа
   * «N ответов» в ветке отставало от строки под корнем.
   */
  const refreshThreadRow = async (channelId: Uuid, threadId: Uuid) => {
    const info = await api.threads.get(channelId, threadId);

    setThreads((previous) =>
      previous[threadId]
        ? {
            ...previous,
            [threadId]: {
              ...previous[threadId],
              reply_count: info.reply_count,
              last_reply_at: info.last_reply_at,
              participants: info.participants,
            },
          }
        : previous,
    );

    if (!info.root) return;

    setFeeds((previous) => {
      const known = previous[channelId];
      if (!known) return previous;

      return {
        ...previous,
        [channelId]: known.map((item) =>
          item.id === info.root!.id
            ? {
                ...item,
                thread: {
                  id: info.id,
                  reply_count: info.reply_count,
                  last_reply_at: info.last_reply_at,
                  participants: info.participants,
                  followed: info.followed,
                  notify_level: info.notify_level,
                  unread: info.unread,
                  mentions: info.mentions,
                  title: info.title ?? null,
                },
              }
            : item,
        ),
      };
    });
  };

  const followThread = async (channelId: Uuid, threadId: Uuid, follow: boolean) => {
    setThreads((previous) =>
      previous[threadId] ? { ...previous, [threadId]: { ...previous[threadId], followed: follow } } : previous,
    );

    await api.threads.follow(channelId, threadId, follow);
    await loadThread(channelId, threadId);
    await refreshThreadRow(channelId, threadId);
    // В ветке те, кто за ней следит: присоединился или покинул — состав другой.
    void queryClient.invalidateQueries({ queryKey: keys.threadMembers(threadId) });
  };

  const setThreadNotify = async (channelId: Uuid, threadId: Uuid, level: NotifyLevel | null) => {
    await api.threads.setNotify(channelId, threadId, level);

    setThreads((previous) =>
      previous[threadId]
        ? { ...previous, [threadId]: { ...previous[threadId], notify_level: level } }
        : previous,
    );

    /*
     * Строка ветки в ленте несёт уровень своим полем — по нему меню рисует
     * галочку. Не обновить её значит показать в меню прошлый выбор: панель
     * знает новый, а строка, из которой меню и открывают, — нет.
     */
    await refreshThreadRow(channelId, threadId);
    setThreadLists((previous) =>
      previous[channelId]
        ? {
            ...previous,
            [channelId]: previous[channelId].map((item) =>
              item.id === threadId ? { ...item, notify_level: level } : item,
            ),
          }
        : previous,
    );
  };

  /**
   * Отметка о прочтении ветки.
   *
   * Счётчики канала гасятся сразу — и ветка с новым, и её упоминания: ждать
   * ответа сервера, чтобы убрать их с уже открытой ветки, значит врать.
   */
  const markThreadRead = async (channelId: Uuid, threadId: Uuid, messageId: Uuid) => {
    noteThread(channelId, threadId, 0, 0);

    setThreads((previous) =>
      previous[threadId]
        ? { ...previous, [threadId]: { ...previous[threadId], unread: 0, last_read_id: messageId } }
        : previous,
    );

    await api.threads.read(channelId, threadId, messageId);
    await refreshThreadRow(channelId, threadId);
  };

  const edit = async (channelId: Uuid, messageId: Uuid, content: string, removeAttachments?: Uuid[]) => {
    put(await api.messages.edit(channelId, messageId, content, removeAttachments));
  };

  // Своё событие не придёт — пометка «опубликовано» берётся из ответа.
  const publish = async (channelId: Uuid, messageId: Uuid) => {
    put(await api.messages.publish(channelId, messageId));
  };

  /**
   * Своё событие сюда не придёт — его отсекает сервер по соединению. Поэтому
   * пересланное кладётся в ленту назначения сразу, если она загружена.
   */
  const forward = async (targetChannelId: Uuid, messageId: Uuid, comment: string) => {
    const sent = await api.messages.forward(targetChannelId, messageId, comment);
    if (get().feeds[targetChannelId] && desk.latest.has(targetChannelId)) put(sent);
  };

  const remove = async (channelId: Uuid, messageId: Uuid) => {
    await api.messages.remove(channelId, messageId);
    setFeeds((previous) => {
      const known = previous[channelId];
      if (!known) return previous;
      return { ...previous, [channelId]: known.filter((item) => item.id !== messageId) };
    });
  };

  const markServerRead = (serverId: Uuid) => {
    for (const [channelId, count] of Object.entries(get().unread)) {
      if (count > 0 && desk.serverOf[channelId] === serverId) markRead(channelId);
    }
  };

  /**
   * Пометка в ленте и список закреплённого — сразу, не дожидаясь события: своё
   * событие сюда не приходит, его отсекает сервер по соединению.
   */
  const markPinned = (channelId: Uuid, messageId: Uuid, value: boolean) => {
    patchMessage(channelId, messageId, (item) => ({ ...item, pinned: value }));
    // Ответ ветки лежит не в ленте канала, а в списке своей ветки.
    setThreadFeeds((previous) => {
      for (const [threadId, replies] of Object.entries(previous)) {
        if (!replies.some((item) => item.id === messageId)) continue;
        return {
          ...previous,
          [threadId]: replies.map((item) => (item.id === messageId ? { ...item, pinned: value } : item)),
        };
      }
      return previous;
    });
    // Закреплённые и канала, и веток: у ветки список свой, и чей это ответ, событие не говорит.
    void queryClient.invalidateQueries({ queryKey: ["pins"] });
  };

  const setPinned = async (channelId: Uuid, messageId: Uuid, value: boolean) => {
    await (value ? api.messages.pin(channelId, messageId) : api.messages.unpin(channelId, messageId));
    markPinned(channelId, messageId, value);
  };

  return {
    ...empty(),
    markRead,
    markUnread,
    releaseKept,
    markServerRead,
    saw,
    setReading,
    setAtEnd,
    newSince: (channelId) => get().since[channelId],
    serverOf: (channelId) => desk.serverOf[channelId],
    levelIn: (channelId) => {
      const server = desk.serverOf[channelId];
      return get().notifyChannel[channelId] ?? (server ? serverLevel(server) : undefined) ?? "all";
    },
    messages: (channelId) => get().feeds[channelId],
    pending: (channelId) => get().queue.filter((item) => item.channelId === channelId),
    loadMessages,
    loadOlder,
    loadNewer,
    // Читается при отрисовке, а меняется вне её: перерисовку даёт лента,
    // которая в этот же миг и приходит.
    atLatest: (channelId) => desk.latest.has(channelId),
    atStart: (channelId) => desk.exhausted.has(channelId),
    jumpToLatest,
    setChannelNotify,
    unmuteChannel,
    setServerNotify,
    setChannelPinned,
    openAt,
    takeJumpTo: (channelId) => {
      const asked = desk.jumpTo[channelId];
      if (!asked) return null;

      delete desk.jumpTo[channelId];
      return asked;
    },
    takeJump: (channelId) => {
      if (desk.jumped !== channelId) return false;
      desk.jumped = null;
      return true;
    },
    send,
    sendSticker,
    sendVoice,
    sendPoll,
    sendPost,
    vote,
    endPoll,
    toggleReaction,
    openThread,
    expandThread,
    reloadThread,
    openThreadList,
    loadThreads: (channelId) => detached("список веток", loadThreadList(channelId, get().threadFilter)),
    closePanel,
    thread: (threadId) => get().threads[threadId],
    threadMessages: (threadId) => get().threadFeeds[threadId],
    threadList: (channelId) => get().threadLists[channelId],
    setThreadFilter: (followed) => {
      setThreadFilterState(followed);
      const { panel } = get();
      if (panel?.kind === "list") detached("список веток", loadThreadList(panel.channelId, followed));
    },
    sendInThread,
    followThread,
    setThreadNotify,
    markThreadRead,
    replyTo: (channelId) => get().replies[channelId],
    setReplyTo: (channelId, message) =>
      setReplies((previous) => ({ ...previous, [channelId]: message ?? undefined })),
    retry,
    drop,
    edit,
    remove,
    forward,
    setPinned,
    publish,

    /*
     * Разбор входящего.
     *
     * Прежде это был эффект провайдера, и всё, что ему нужно, доставалось из
     * зеркал: подписка живёт одной на весь сеанс, а состояние менялось под ней.
     * Изнутри стора зеркала не нужны — `get()` и `desk` всегда свежие.
     */
    listen: () => {
      const me = useSession.getState().me;
      if (!me) return;

      return realtime.on((event: RealtimeEvent) => {
        switch (event.context) {
          case "message": {
            // Список постов форума — по любому движению в канале: новый пост,
            // ответ в ветке, удаление.
            const touched = (event.payload as { channel_id?: Uuid }).channel_id;
            if (touched) refreshPosts(touched);

            if (event.action === "delete") {
              const payload = event.payload as { id: Uuid; channel_id: Uuid };
              /*
               * Удалили ещё непрочитанное — счётчик должен убавиться, как в
               * Discord, а не держать пропавшее до перезапуска. Что именно
               * убавить (и было ли там упоминание), знает только сервер:
               * удаление редкое, и сводку проще переспросить.
               */
              const read = desk.lastRead[payload.channel_id];
              if ((get().unread[payload.channel_id] ?? 0) > 0 && (!read || payload.id > read)) {
                // Бан с очисткой шлёт удаление на каждое сообщение — один запрос на всю пачку.
                window.clearTimeout(desk.unreadAfterDelete);
                desk.unreadAfterDelete = window.setTimeout(() => void reloadUnread().catch(() => {}), 300);
              }
              setFeeds((previous) => {
                const known = previous[payload.channel_id];
                if (!known) return previous;
                return {
                  ...previous,
                  [payload.channel_id]: known.filter((item) => item.id !== payload.id),
                };
              });
              return;
            }
            const message = event.payload as Message;

            /*
             * Ответ ветки в ленту канала не попадает — ни в окно, ни в счётчик.
             * Своё место у него в открытой панели, и только если открыта та
             * самая ветка: остальное принесёт событие ветки, у него и числа
             * правильные.
             */
            if (message.thread_id) {
              const threadId = message.thread_id;

              setThreadFeeds((previous) =>
                previous[threadId]
                  ? { ...previous, [threadId]: merge(previous[threadId], [message]) }
                  : previous,
              );
              return;
            }

            put(message);

            // Своё сообщение непрочитанным не считается.
            if (message.author.id === me.id) return;
            // Эфемерный ответ бота — на свой же вызов: он перед глазами, звать незачем.
            if (message.ephemeral) return;

            const here = desk.reading === message.channel_id;

            /*
             * Пришедшее в открытый канал, конец которого перед глазами, не
             * зажигает счётчик и не зовёт оповещением: оно появилось прямо под
             * взглядом. Отметку поставит наблюдатель, когда строка отрисуется, —
             * отмечать здесь же значило бы завести второе место, где это
             * делается.
             *
             * «Конец перед глазами» — это и низ прокрутки, и низ ленты разом:
             * окно, не доходящее до самого свежего, кончается серединой
             * разговора, и увидеть в нём приходящее нельзя.
             */
            if (here && desk.atEnd && desk.latest.has(message.channel_id)) return;

            // Первое непрочитанное — вот оно, если до сих пор такого не было.
            desk.firstUnread[message.channel_id] ??= message.id;

            setUnread((previous) => ({
              ...previous,
              [message.channel_id]: (previous[message.channel_id] ?? 0) + 1,
            }));

            /*
             * Упоминание распознаём по своему полному хэндлу — тому же, по
             * которому его разбирает сервер. Считать заново по своим правилам
             * значило бы завести второй разбор, который рано или поздно
             * разойдётся с первым.
             */
            /*
             * Позвали ли — по списку с сервера: имя, роль, ответ с «@ВКЛ». Счёт
             * здесь обязан сойтись со сводкой после перезагрузки, а её сервер
             * считает ровно по этому списку. Хэндл в тексте — на случай события
             * от узла постарше, где списка ещё нет.
             */
            const named =
              (message.mentions ?? []).includes(me.id) || message.content.includes(`@${me.handle}`);
            if (named) {
              setMentions((previous) => ({
                ...previous,
                [message.channel_id]: (previous[message.channel_id] ?? 0) + 1,
              }));
            }

            /*
             * Звать или молчать — решает правило из `entities/channel`: в нём сходятся
             * режим присутствия и выбор по каналу и серверу, и побеждает более
             * тихое. Счётчик при этом растёт в любом случае: настройка глушит
             * оклик, а не правду о том, что сообщение было.
             */
            const server = desk.serverOf[message.channel_id];
            const loud = shouldCall(
              named,
              me.status_presence,
              get().notifyChannel[message.channel_id],
              server ? serverLevel(server) : undefined,
            );

            // Тихое (@silent) счётчик двигает, но не будит — ни оповещением, ни звуком.
            // Заблокированный не будит тоже: в ленте его строки свёрнуты, как в Discord.
            const blocked = useCatalogue.getState().relationships.some(
              (user) => user.id === message.author.id && user.relationship === "blocked",
            );
            if (loud && !message.silent && !blocked) {
              const who = message.author.display_name || message.author.username;
              /*
               * В оповещении разметка не рисуется, поэтому и не показывается:
               * «**срочно**» в системном окне читается хуже, чем «срочно».
               */
              /*
               * Нажатие ведёт к самому сообщению — тем же адресом, что ссылка на
               * него: переход по нему оболочка уже умеет, второй путь не нужен.
               */
              const link = messageLink({
                serverId: server ?? null,
                channelId: message.channel_id,
                messageId: message.id,
              });
              // У системного текста нет: оповещение говорит само за себя.
              const body =
                message.kind === "join"
                  ? "теперь на сервере"
                  : message.kind === "call"
                    ? "Входящий звонок"
                    : plainText(message.content).slice(0, 160);
              void notify(who, body, () => {
                location.hash = link.slice(link.indexOf("#"));
              });

              /*
               * Звук идёт под тем же условием и рядом, а не своей проверкой:
               * «звать или молчать» — вопрос один, и разводить его на два
               * места значило бы завести второе правило, которое рано или
               * поздно разойдётся с первым.
               *
               * При этом звук не заменяет оповещение и не заменяется им:
               * системное окно человек может и не увидеть — оно уходит за
               * край или отключено самой системой, — а окно приложения может
               * быть открыто прямо перед ним, и тогда оповещения не будет
               * вовсе, а знать о сообщении в другом канале всё равно надо.
               *
               * У звонка свой звук — мелодия вызова, её играет окно звонка.
               */
              if (message.kind !== "call") cue("notify");
            }

            /*
             * Ушли вверх по истории — черта заводится прямо сейчас, перед этим
             * сообщением. Иначе, вернувшись вниз, человек не отличит новое от
             * того, что уже видел.
             */
            if (here) {
              setSince((previous) =>
                previous[message.channel_id] === undefined
                  ? { ...previous, [message.channel_id]: message.id }
                  : previous,
              );
            }
            return;
          }

          case "post": {
            // Пост закрыли или закрепили: список — заново, открытая ветка — тоже,
            // чтобы поле ответа погасло сразу, а не после отказа.
            const payload = event.payload as { channel_id: Uuid; thread_id: Uuid };
            refreshPosts(payload.channel_id);
            const panel = get().panel;
            if (panel?.kind === "thread" && panel.threadId === payload.thread_id) {
              detached("перечитать ветку", loadThread(payload.channel_id, payload.thread_id));
            }
            return;
          }

          case "reminder": {
            /*
             * Сработало своё напоминание — как в Discord: оповещение с самим
             * сообщением, нажатие ведёт к нему. Текст берётся из свежего списка
             * напоминаний: в событии только номера.
             */
            const payload = event.payload as { message_id: Uuid; channel_id: Uuid; server_id: Uuid | null };
            void (async () => {
              await queryClient.invalidateQueries({ queryKey: keys.reminders() });
              const list = await queryClient
                .fetchQuery({ queryKey: keys.reminders(), queryFn: () => api.users.reminders() })
                .catch(() => []);
              const item = list.find((entry) => entry.message.id === payload.message_id);
              const who = item ? item.message.author.display_name || item.message.author.username : null;
              const text = item ? plainText(item.message.content).slice(0, 160) : "";
              const link = messageLink({
                serverId: payload.server_id,
                channelId: payload.channel_id,
                messageId: payload.message_id,
              });
              if (shouldCall(true, me.status_presence, undefined, undefined)) {
                void notify("Напоминание", who ? `${who}: ${text || "сообщение"}` : "Пора вернуться к сообщению", () => {
                  location.hash = link.slice(link.indexOf("#"));
                });
                cue("notify");
              }
            })();
            return;
          }

          case "scheduled_event": {
            // Список событий у каждого свой (закрытые каналы), поэтому только номер сервера.
            const payload = event.payload as {
              server_id: Uuid;
              started?: { id: Uuid; name: string; channel_id: Uuid | null };
            };
            void queryClient.invalidateQueries({ queryKey: keys.events(payload.server_id) });

            /*
             * Напоминание о начале — нам лично: мы откликнулись «интересно».
             * Поэтому зовёт как упоминание — молчит только «не беспокоить» или
             * выключенный сервер.
             */
            if (
              payload.started &&
              shouldCall(true, me.status_presence, undefined, serverLevel(payload.server_id))
            ) {
              void notify("Событие началось", payload.started.name);
              cue("notify");
            }
            return;
          }

          case "poll": {
            /*
             * Новый счёт опроса. Свой голос событие не несёт — он у нас уже есть
             * (из ответа на свой голос или из ленты), — поэтому меняются только
             * числа.
             */
            const payload = event.payload as {
              channel_id: Uuid;
              message_id: Uuid;
              answers: { id: Uuid; votes: number }[];
              /** Есть, когда опрос закончили досрочно. */
              expires?: string;
            };
            const counts = new Map(payload.answers.map((answer) => [answer.id, answer.votes]));
            patchMessage(payload.channel_id, payload.message_id, (item) =>
              item.poll
                ? {
                    ...item,
                    poll: {
                      ...item.poll,
                      expires: payload.expires ?? item.poll.expires,
                      answers: item.poll.answers.map((answer) => ({
                        ...answer,
                        votes: counts.get(answer.id) ?? answer.votes,
                      })),
                    },
                  }
                : item,
            );
            return;
          }

          case "reaction": {
            const payload = event.payload as {
              message_id: Uuid;
              channel_id: Uuid;
              user_id: Uuid;
              emoji: string;
            };

            patchMessage(payload.channel_id, payload.message_id, (item) =>
              withReaction(
                item,
                payload.emoji,
                event.action === "create",
                payload.user_id === me.id,
              ),
            );
            return;
          }

          case "pin": {
            const payload = event.payload as { message_id: Uuid; channel_id: Uuid };
            markPinned(payload.channel_id, payload.message_id, event.action === "create");
            return;
          }

          case "thread": {
            if (event.action === "delete") {
              const gone = event.payload as { id: Uuid; channel_id: Uuid; root_message_id: Uuid | null };
              /*
               * Ветку удалили: строка под корнем пропадает, открытая колонка
               * закрывается — смотреть в ней больше не на что. Её непрочитанное
               * и упоминания сидели в счётчиках канала, и сколько именно, знает
               * только сервер: сводку проще переспросить, как после удаления
               * сообщения.
               */
              if (get().panel?.threadId === gone.id) setPanel(null);
              setFeeds((previous) => {
                const known = previous[gone.channel_id];
                if (!known || !gone.root_message_id) return previous;
                return {
                  ...previous,
                  [gone.channel_id]: known.map((item) =>
                    item.id === gone.root_message_id ? { ...item, thread: null } : item,
                  ),
                };
              });
              setThreadLists((previous) =>
                previous[gone.channel_id]
                  ? { ...previous, [gone.channel_id]: previous[gone.channel_id].filter((item) => item.id !== gone.id) }
                  : previous,
              );
              delete desk.threadSeen[gone.id];
              delete desk.threadNamed[gone.id];
              window.clearTimeout(desk.unreadAfterDelete);
              desk.unreadAfterDelete = window.setTimeout(() => void reloadUnread().catch(() => {}), 300);
              return;
            }

            /*
             * Событий о ветке два.
             *
             * Общее идёт в комнату канала и несёт рост: сколько ответов, когда
             * последний, кто говорил. Личное идёт подписчику и добавляет к
             * этому его собственные числа. Врозь они потому, что счёт
             * непрочитанного ведётся от отметки смотрящего, а комната одна на
             * всех.
             *
             * Различаются они наличием этих чисел, а не отдельным признаком.
             * Признак тут был, и на нём же всё сломалось: узел постарше его не
             * слал, личное событие принималось за общее — строка появлялась, а
             * «сколько новых» в ней не было. Числа же есть ровно у того
             * события, у которого им и место.
             */
            const payload = event.payload as {
              id: Uuid;
              channel_id: Uuid;
              root_message_id: Uuid | null;
              reply_count: number;
              last_reply_at: string | null;
              participants?: MessageAuthor[];
              notify_level?: NotifyLevel | null;
              unread?: number;
              mentions?: number;
              followed?: boolean;
              /** Имя ветки; нет поля — узел постарше, имя не трогаем. */
              title?: string | null;
              /** Закрыта, заблокирована, медленный режим — как у имени: нет поля, не трогаем. */
              owner_id?: Uuid | null;
              closed?: boolean;
              locked?: boolean;
              slowmode?: number;
              notify_until?: string | null;
            };

            /** Рост ветки — то, что в обоих событиях одинаково. Имя — тоже, если пришло. */
            const grown = {
              id: payload.id,
              reply_count: payload.reply_count,
              last_reply_at: payload.last_reply_at,
              ...(payload.title !== undefined ? { title: payload.title } : {}),
              ...(payload.owner_id !== undefined ? { owner_id: payload.owner_id } : {}),
              ...(payload.closed !== undefined ? { closed: payload.closed } : {}),
              ...(payload.locked !== undefined ? { locked: payload.locked } : {}),
              ...(payload.slowmode !== undefined ? { slowmode: payload.slowmode } : {}),
            };

            /*
             * Лица — с запасом на узел, который их не шлёт.
             *
             * Поле новое, и старый узел события им не подписывает. Взять
             * оттуда пустоту значило бы стереть лица у того, у кого они уже
             * есть, а взять `undefined` — уронить отрисовку строки: она
             * режет список сразу, без проверки.
             */
            const faces = payload.participants ?? null;

            /**
             * Свои числа — только из личного события.
             *
             * В общем их нет вовсе, и подставить туда нули значило бы погасить
             * ветку у того, кто за ней следит: два события об одном ответе
             * приходят в любом порядке.
             */
            const own =
              payload.unread === undefined
                ? null
                : {
                    followed: payload.followed ?? false,
                    notify_level: payload.notify_level ?? null,
                    unread: payload.unread,
                    mentions: payload.mentions ?? 0,
                    ...(payload.notify_until !== undefined ? { notify_until: payload.notify_until } : {}),
                  };

            // В событии — числа ветки целиком, а не прибавка: канал правится на разницу.
            if (own) noteThread(payload.channel_id, payload.id, own.unread, own.mentions);

            // Ответивший начинает следить за веткой — состав перечитывается, если он на экране.
            void queryClient.invalidateQueries({ queryKey: keys.threadMembers(payload.id) });

            setThreads((previous) =>
              previous[payload.id]
                ? {
                    ...previous,
                    [payload.id]: {
                      ...previous[payload.id],
                      ...grown,
                      participants: faces ?? previous[payload.id].participants,
                      ...own,
                    },
                  }
                : previous,
            );

            // Строка ветки под корнем — если корень в загруженном окне.
            if (payload.root_message_id) {
              setFeeds((previous) => {
                const known = previous[payload.channel_id];
                if (!known) return previous;

                return {
                  ...previous,
                  [payload.channel_id]: known.map((item) =>
                    item.id === payload.root_message_id
                      ? {
                          ...item,
                          /*
                           * Строка заводится прямо здесь, если её ещё нет:
                           * первый ответ — это и есть появление ветки, ждать
                           * со строкой нечего. Правилась она только готовая, и
                           * оттого у второго человека ветка не показывалась до
                           * перезагрузки.
                           */
                          thread: {
                            ...grown,
                            participants: faces ?? item.thread?.participants ?? [],
                            followed: item.thread?.followed ?? false,
                            notify_level: item.thread?.notify_level ?? null,
                            unread: item.thread?.unread ?? 0,
                            mentions: item.thread?.mentions ?? 0,
                            title: item.thread?.title ?? null,
                            ...grown,
                            ...own,
                          },
                        }
                      : item,
                  ),
                };
              });
            }
            return;
          }

          case "read": {
            const payload = event.payload as { channel_id: Uuid; last_read_id: Uuid; back?: boolean };
            const channelId = payload.channel_id;

            // Отмечено непрочитанным — на другом устройстве или здесь же (эхо).
            // Назад обычная отметка не едет, поэтому сводку просто спрашиваем.
            if (payload.back) {
              if (desk.lastRead[channelId] !== payload.last_read_id) void reloadUnread();
              return;
            }

            const feed = get().feeds[channelId];
            const at = feed?.findIndex((item) => item.id === payload.last_read_id) ?? -1;
            const here =
              feed && desk.lastRead[channelId]
                ? feed.findIndex((item) => item.id === desk.lastRead[channelId])
                : -1;

            /*
             * Назад отметка не едет — ни своим эхом, ни опоздавшим событием.
             *
             * Заголовок с номером соединения клиент не шлёт, поэтому событие
             * возвращается и к тому, кто отметку поставил, — а поставил он её,
             * пока читал дальше. Сдвинуть по такому событию счётчик назад значит
             * снова показать непрочитанным то, что человек только что прочёл.
             */
            if (at >= 0 && at <= here) return;

            /*
             * Прочитали с другого устройства. Сколько осталось — считаем по своей
             * ленте, но только если она доходит до конца: отметка одна на канал, и
             * тогда непрочитанным осталось ровно то, что ниже неё. Иначе за
             * ответом идём на сервер — окно кончается серединой разговора, и
             * считать по нему остаток нельзя.
             */
            if (at < 0 || !desk.latest.has(channelId)) {
              void reloadUnread();
              return;
            }

            desk.lastRead[channelId] = payload.last_read_id;

            const left = feed!.slice(at + 1).filter((item) => item.author.id !== me.id);
            const named = left.filter((item) => item.content.includes(`@${me.handle}`)).length;

            desk.firstUnread[channelId] = left[0]?.id ?? null;
            setUnread((previous) => ({ ...previous, [channelId]: left.length }));
            setMentions((previous) => ({ ...previous, [channelId]: named + threadMentions(channelId) }));
            return;
          }

          case "typing": {
            const payload = event.payload as { channel_id: Uuid; user_id: Uuid };

            useChat.setState((state) => ({
              typing: {
                ...state.typing,
                [payload.channel_id]: {
                  ...(state.typing[payload.channel_id] ?? {}),
                  [payload.user_id]: Date.now() + TYPING_TTL,
                },
              },
            }));

            if (sweeper === null) sweeper = setInterval(sweepTyping, 1000);

            return;
          }

          case "presence": {
            const payload = event.payload as { user_id: Uuid; online: boolean };
            useCatalogue.getState().setPresence(payload.user_id, payload.online);
            return;
          }

          case "user": {
            /*
             * Профиль изменился у того, кого мы видим: имя, текст статуса,
             * режим присутствия. Событие несёт один идентификатор, поэтому
             * списки перезапрашиваются целиком — там же, где они и живут.
             * Дорисовывать половину полей руками значит разойтись с сервером.
             */
            /*
             * Свой профиль — тоже: его правят с другого устройства, а статус
             * снимается сам по сроку, и полоса себя иначе показывала бы старое.
             */
            const changed = (event.payload as { user_id?: Uuid }).user_id;
            // Карточка и окно профиля держат человека своим запросом: баннер, «о себе».
            if (changed) void queryClient.invalidateQueries({ queryKey: keys.user(changed) });
            if (changed === me.id) {
              // Раскладку ряда поменяли на другом устройстве — только её и перечитать.
              if ((event.payload as { rail?: boolean }).rail) {
                void queryClient.invalidateQueries({ queryKey: keys.rail() });
                return;
              }
              void useSession.getState().refreshMe();
            }
            void useCatalogue.getState().reloadRelationships();
            /*
             * Переписки тоже: собеседник в них лежит снимком профиля, набранным
             * при загрузке списка. Мало того что снимок устаревал, он ещё и
             * ложится поверх отношений в `peopleById` — то есть свежий профиль
             * друга перекрывался старым, если с ним заведена переписка.
             */
            void useCatalogue.getState().reloadDirects();
            /*
             * Какого сервера касается — неизвестно, да и незачем: помечаем
             * состав всех, что попадались, а перезапросит его только тот
             * экран, который сейчас открыт. Остальное так и останется
             * помеченным до случая.
             */
            for (const id of new Set(Object.values(desk.serverOf))) {
              if (id) void queryClient.invalidateQueries({ queryKey: keys.members(id) });
            }
            return;
          }

          case "relationship": {
            // Событие несёт урезанный профиль, поэтому список перезапрашивается
            // целиком: дорисовывать половину полей руками — верный способ
            // разойтись с сервером.
            void useCatalogue.getState().reloadRelationships();
            return;
          }

          case "sound": {
            /*
             * Звук панели слышат только те, кто в этом звонке: событие уходит
             * в комнату канала, а её видят и те, кто просто смотрит на канал.
             */
            const payload = event.payload as SoundPlayed;
            if (heldRoom()?.name === payload.channel_id) playSound(payload.file_id, payload.volume);
            return;
          }

          case "voice": {
            const payload = event.payload as {
              channel_id: Uuid;
              user_id: Uuid;
              microphone?: boolean;
              deafened?: boolean;
              live?: boolean;
              moved_to?: Uuid;
              server_muted?: boolean;
              server_deafened?: boolean;
              ringing?: CallRing[];
              speaker?: boolean;
              hand?: string | null;
            };

            // Кого вызывают в звонок переписки — список целиком.
            if (payload.ringing) {
              useCatalogue.getState().setRings(payload.channel_id, payload.ringing);
              return;
            }

            // Нас перенёс модератор. Это не правка состояния: переходит звонок.
            if (payload.moved_to) {
              useCatalogue.getState().setMovedTo({ from: payload.channel_id, to: payload.moved_to });
              return;
            }

            /*
             * Правка — это переключённый микрофон, звук или показ. Приходит
             * она чаще, чем вход и выход, и от неё состав не меняется:
             * заводить и убирать людей ей нельзя, иначе опоздавшее событие
             * вернёт в канал того, кто уже вышел.
             */
            // Модератор заглушил или оглушил на сервере — меняются только эти признаки.
            if (event.action === "update" && payload.server_muted !== undefined) {
              const known = useCatalogue.getState().calls[payload.channel_id]?.find((item) => item.id === payload.user_id);
              if (known) {
                useCatalogue.getState().setVoiceState(payload.channel_id, {
                  ...known,
                  serverMuted: payload.server_muted,
                  serverDeafened: !!payload.server_deafened,
                });
              }
              return;
            }

            // Положение на сцене: выступает ли и поднята ли рука.
            if (event.action === "update" && payload.speaker !== undefined) {
              const known = useCatalogue.getState().calls[payload.channel_id]?.find((item) => item.id === payload.user_id);
              if (known) {
                useCatalogue.getState().setVoiceState(payload.channel_id, {
                  ...known,
                  speaker: payload.speaker,
                  hand: payload.hand ?? null,
                });
              }
              // Нас позвали выступать или вернули в зал — права в канале другие.
              if (payload.user_id === me.id) {
                void queryClient.invalidateQueries({ queryKey: keys.channelRights(payload.channel_id) });
              }
              return;
            }

            if (event.action === "update") {
              useCatalogue.getState().setVoiceState(payload.channel_id, {
                id: payload.user_id,
                microphone: !!payload.microphone,
                deafened: !!payload.deafened,
                live: !!payload.live,
              });
              return;
            }

            useCatalogue
              .getState()
              .setVoice(payload.channel_id, payload.user_id, event.action === "delete", {
                serverMuted: !!payload.server_muted,
                serverDeafened: !!payload.server_deafened,
              });
            return;
          }

          case "channel": {
            const payload = event.payload as { channel_type?: string; server_id?: Uuid };

            // Личная переписка приходит сюда, когда её открыл собеседник:
            // список переписок иначе обновился бы только перезагрузкой.
            if (payload.channel_type === "direct" || payload.channel_type === "group") {
              void useCatalogue.getState().reloadDirects();
              return;
            }

            /*
             * Дерево сервера: имя канала, порядок, категория, появление и
             * удаление. Событие несёт только номер сервера — имён в нём нет
             * намеренно, потому что канал бывает скрыт от части участников.
             * Что именно человеку видно, решает перезапрос: он отфильтрован
             * правами.
             */
            if (payload.server_id) {
              reloadTreeSoon(payload.server_id);
              return;
            }

            void useCatalogue.getState().reloadServers();
            return;
          }

          case "server": {
            if (event.action === "update") {
              const payload = event.payload as { server_id: Uuid; user_id?: Uuid; rights?: boolean };

              /*
               * С именем человека — это изменившийся состав: кто-то вступил.
               * Список серверов от этого не меняется, а вот состав открытого
               * устарел, и без отметки он оставался бы вчерашним.
               */
              if (payload.user_id) {
                void queryClient.invalidateQueries({ queryKey: keys.members(payload.server_id) });
                // Про нас самих — могли смениться и права: заглушение, тайм-аут.
                if (payload.user_id === me.id) void queryClient.invalidateQueries({ queryKey: ["rights"] });
                return;
              }

              /*
               * Без имени — сменился сам сервер: имя, значок, описание. Всё
               * это лежит в списке серверов, а не в состоянии открытого:
               * шапка и полоса берут имя именно оттуда. Правила и тег живут
               * в состоянии сервера и меняют права — их тоже перечитать.
               */
              void useCatalogue.getState().reloadServers();
              if (payload.rights) {
                reloadTreeSoon(payload.server_id);
                void queryClient.invalidateQueries({ queryKey: ["rights"] });
              }
              return;
            }

            /*
             * Нас убрали с сервера. Признак `removed` обязателен: тем же видом
             * события когда-нибудь поедет и удаление сервера целиком, а это
             * другой разговор — там некому говорить «вас исключили».
             */
            if (event.action === "delete") {
              const payload = event.payload as {
                server_id?: Uuid;
                removed?: boolean;
                banned?: boolean;
              };

              if (payload.removed && payload.server_id) {
                useCatalogue.getState().setRemovedFrom({
                  serverId: payload.server_id,
                  banned: !!payload.banned,
                });
              }
            }

            void useCatalogue.getState().reloadServers();
            return;
          }

          default:
            return;
        }
      });
    },

    start: () => {
      const catalogue = useCatalogue.getState();
      void catalogue.reloadServers();
      void catalogue.reloadDirects();
      void catalogue.reloadRelationships();
      void reloadUnread();
      void reloadNotify();
    },

    typingIn: (channelId) => {
      const now = Date.now();
      const people = get().typing[channelId] ?? {};

      /*
       * Срок проверяется и на чтении, а не только уборкой: между тиком
       * уборщика и отрисовкой проходит до секунды, и всё это время надпись
       * висела бы уже просроченной.
       */
      return Object.entries(people)
        .filter(([, until]) => until > now)
        .map(([userId]) => userId);
    },

    forget: () => {
      stopSweeping();
      forgetTreeReloads();
      clearDesk();
      // Таймер «заглушить на время» прежнего входа — не следующему.
      clearTimeout(unmuteTimer.current);
      set(empty());
    },
  };
});

/**
 * Работа стора, а не его содержимое: подписка на события и первичная загрузка.
 *
 * Состояние уехало в модуль, и держать его провайдеру больше не надо. Осталось
 * то, чего модуль сам не умеет, — начать при входе и прекратить при выходе.
 */
export function StoreProvider({ children }: { children: ReactNode }) {
  const me = useSession((state) => state.me);
  const connection = useCatalogue((state) => state.connection);
  const outgoing = useChat((state) => state.queue);
  const failed = outgoing.filter((message) => message.failed).length;

  useRestartBlocker(
    outgoing.length > 0
      ? {
          id: "outgoing-messages",
          title: failed > 0 ? "Есть неотправленные сообщения" : "Сообщение отправляется",
          detail:
            failed > 0
              ? "повторите отправку или удалите сообщение из очереди"
              : "дождитесь подтверждения сервера",
          kind: "wait",
        }
      : null,
  );

  useEffect(() => realtime.onState(useCatalogue.getState().setLink), []);

  /*
   * Связь вернулась после обрыва — перечитываем то, что знаем по событиям:
   * пропущенные, пока её не было, не придут уже никогда. Без этого тот, кто
   * вошёл в звонок, пока у нас переподключался сокет, так и не появлялся под
   * каналом, хотя в самом звонке его видно и слышно. «live» приходит раз на
   * соединение, поэтому второй — это уже переподключение.
   */
  useEffect(() => {
    let connected = false;
    return realtime.onState((state) => {
      if (state !== "live") return;
      if (connected) {
        const catalogue = useCatalogue.getState();
        for (const serverId of Object.keys(catalogue.states)) {
          detached("перечитать сервер после обрыва связи", catalogue.loadServer(serverId));
        }
        detached("перечитать переписки после обрыва связи", catalogue.reloadDirects());
      }
      connected = true;
    });
  }, []);

  /*
   * Про себя присутствие событием не приходит: рассылка идёт друзьям, а не
   * себе. Без этого собственное имя висело бы в группе «не в сети» — на
   * своём же экране, где видно, что связь есть.
   */
  useEffect(() => {
    if (!me) return;

    const visible = connection === "live" && me.status_presence !== "invisible";

    useCatalogue.getState().setPresence(me.id, visible);
    /*
     * Зависимость по `me?.id`, а не по самому человеку: объект приезжает новым
     * на каждую правку профиля, а переподписываться надо только тогда, когда
     * сменился человек.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, me?.status_presence, connection]);

  useEffect(() => {
    if (!me) return;
    return useChat.getState().listen();
    /*
     * Зависимость по `me?.id`, а не по самому человеку: объект приезжает новым
     * на каждую правку профиля, а переподписываться надо только тогда, когда
     * сменился человек.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  useEffect(() => {
    if (!me) return;
    useChat.getState().start();
    /*
     * Зависимость по `me?.id`, а не по самому человеку: объект приезжает новым
     * на каждую правку профиля, а переподписываться надо только тогда, когда
     * сменился человек.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  /*
   * Уборка при выходе.
   *
   * Прежде она выходила бесплатно: провайдер держал всё состояние и уходил
   * вместе с ним, когда `Gate` показывал вход. Оба стора живут в модуле и сами
   * не уйдут — без этого следующий вошедший на той же вкладке увидел бы чужие
   * серверы и чужую переписку.
   *
   * Кэш запросов чистится здесь же и ровно по той же причине: в нём лежат
   * состав серверов, права и список сессий — всё чужое. Провайдер `QueryClient`
   * стоит выше входа и с выходом не уходит, так что расчёта на его размонтаж
   * тут нет.
   *
   * Ряд быстрых реакций — из того же ряда чужого: он собран из того, чем
   * отвечает этот человек, и следующему вошедшему достался бы вместе с
   * вкладкой.
   */
  useEffect(
    () => () => {
      useCatalogue.getState().reset();
      useChat.getState().forget();
      forgetQuick();
      queryClient.clear();
    },
    [],
  );

  return <>{children}</>;
}

/**
 * Общее значение: каталог и всё остальное вместе.
 *
 * Стора два, а мест вызова — три десятка, и делить их по сторам значило бы
 * править каждое. Склейка держится на `useMemo`: новый объект появляется
 * только когда в одном из сторов что-то изменилось, а на это место опираются
 * зависимости эффектов на экранах.
 */
export function useStore(): StoreValue {
  const chat = useChat();
  const catalogue = useCatalogue();
  return useMemo(() => ({ ...catalogue, ...chat }), [catalogue, chat]);
}

/**
 * Кладёт выбор в запись или убирает его.
 *
 * Убирает, а не пишет умолчание: «как выше» — это отсутствие записи, и
 * записанное «all» значило бы совсем другое.
 */
function chosen<T>(known: Record<Uuid, T>, key: Uuid, value: T | null): Record<Uuid, T> {
  if (value === null) {
    if (!(key in known)) return known;
    const next = { ...known };
    delete next[key];
    return next;
  }
  return { ...known, [key]: value };
}

/** История приходит от новых к старым, а лента читается сверху вниз. */
function sortAsc(items: Message[]): Message[] {
  return [...items].sort((a, b) => a.created.localeCompare(b.created) || a.id.localeCompare(b.id));
}

/**
 * Слияние без дублей: то же сообщение может прийти и ответом на отправку, и
 * событием по вебсокету, и из истории.
 */
/**
 * Прибавляет или убирает один значок.
 *
 * Считается на клиенте, а не приходит числом: событие несёт «кто и что
 * поставил», и по нему счёт получается точным — в том числе `mine`, который у
 * каждого смотрящего свой и в общую рассылку попасть не может.
 */
function withReaction(message: Message, emoji: string, added: boolean, isMe: boolean): Message {
  const reactions = [...message.reactions];
  const index = reactions.findIndex((item) => item.emoji === emoji);

  if (added) {
    if (index < 0) return { ...message, reactions: [...reactions, { emoji, count: 1, mine: isMe }] };

    const current = reactions[index];
    // Своё повторное нажатие ничего не добавляет — сервер тоже не удваивает.
    if (isMe && current.mine) return message;

    reactions[index] = { ...current, count: current.count + 1, mine: current.mine || isMe };
    return { ...message, reactions };
  }

  if (index < 0) return message;

  const current = reactions[index];
  if (isMe && !current.mine) return message;

  const count = current.count - 1;
  if (count <= 0) {
    reactions.splice(index, 1);
    return { ...message, reactions };
  }

  reactions[index] = { ...current, count, mine: isMe ? false : current.mine };
  return { ...message, reactions };
}

function merge(known: Message[], incoming: Message[]): Message[] {
  const byId = new Map(known.map((item) => [item.id, item]));
  for (const item of incoming) byId.set(item.id, item);
  return sortAsc([...byId.values()]);
}
